import { EnterForm } from '../components/EnterForm';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFY, isDateInFY, getFYDateError } from '../context/FYContext';
import { INDIAN_STATES } from './GST';
import { usePageCallbacks } from '../hooks/usePageCallbacks';
import {
  getCustomerFirms, createCustomerFirm, updateCustomerFirm, deleteCustomerFirm,
  bulkDeleteCustomerFirms,
  getSalesOrders, getLastSalesOrder, createSalesOrder, updateSalesOrder, deleteSalesOrder,
  getSalesEntries, createSalesEntry, updateSalesEntry, deleteSalesEntry,
  bulkDeleteSalesOrders, bulkDeleteSalesEntries,
  getSOOutstanding, getSalesPaymentOutstanding,
  getSaleReceipts, createSaleReceipt, clearSaleReceipt, bounceSaleReceipt, deleteSaleReceipt,
  getCustomerLedgerSummary, getCustomerLedgerDetail,
  getProducts, createProduct, updateProduct
} from '../lib/api';
import api, { getHSNCodes, getCompanySettings, getHSNRateForDate } from '../lib/api';
import { formatCurrency, formatDate, getToday, getDateRange, formatApiErrorDetail } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { SectionTiles } from '../components/SectionTiles';
import { Plus, Package, Truck, AlertCircle, Pencil, Trash2, ChevronDown, ChevronUp, Building2, ClipboardList, IndianRupee, FileCheck, BookOpen, FileText, Printer } from 'lucide-react';
import { ExportToolbar } from '../components/ui/export-toolbar';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DateInput } from '../components/ui/date-input';
import { PhoneInput } from '../components/ui/phone-input';
import { DateRangeFilter } from '../components/ui/date-range-filter';
import { useSortableTable, SortableHeader } from '../components/ui/sortable-table';
import { FormShortcutHints } from '../components/ui/form-shortcut-hints';
import { toast } from 'sonner';

const emptyContact = { name: '', department: '', mobile_number: '', email: '' };
const emptyLineItem = { product_id: '', description: '', hsn_code: '', quantity_kg: '', quantity_bags: '', rate: '', brand: '', gst_rate: '', cgst_amount: '', sgst_amount: '', igst_amount: '', tax_type: '' };
const toTons = (kg) => kg ? `${(kg / 1000).toFixed(2)} Tons` : '0 Tons';

const Sales = () => {
  const { isAdmin } = useAuth();
  const { selectedFY, fyStartDate, fyEndDate, shortLabel: fyLabel } = useFY();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.state?.tab || '';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Listen for quick-open tab switch events
  useEffect(() => {
    const handleQuickOpenTab = (e) => {
      const tabMap = { entries: 'sales-entries', customers: 'customers', outstanding: 'outstanding', orders: 'sales-orders' };
      const tab = tabMap[e.detail?.tab] || e.detail?.tab;
      if (tab && TABS.includes(tab)) setActiveTab(tab);
    };
    window.addEventListener('quickopen-tab', handleQuickOpenTab);
    return () => window.removeEventListener('quickopen-tab', handleQuickOpenTab);
  }, []); // eslint-disable-line

  const TABS = ['sales-orders', 'sales-entries', 'outstanding', 'receipt-outstanding', 'receipts', 'customers'];
  const SECTION_DEFS = [
    { key: '1', value: 'sales-orders', label: 'Sales Orders', icon: Package },
    { key: '2', value: 'sales-entries', label: 'Sales', icon: Truck },
    { key: '3', value: 'outstanding', label: 'SO Outstanding', icon: ClipboardList },
    { key: '4', value: 'receipt-outstanding', label: 'Sales Outstanding', icon: IndianRupee },
    { key: '5', value: 'receipts', label: 'Receipts', icon: FileCheck },
    { key: '6', value: 'customer-ledger', label: 'Customer Ledger', icon: BookOpen },
    { key: '7', value: 'customers', label: 'Customers', icon: Building2 },
    { key: '8', value: 'notes', label: 'Credit Notes', icon: FileText },
  ];
  const switchTab = (direction) => {
    setActiveTab(t => {
      if (!t) return TABS[0];
      const i = TABS.indexOf(t);
      return direction === 'next'
        ? TABS[(i + 1) % TABS.length]
        : TABS[(i - 1 + TABS.length) % TABS.length];
    });
  };

  // Shared data
  const [customerFirms, setCustomerFirms] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // SO state
  const [salesOrders, setSalesOrders] = useState([]);
  const [allSalesOrders, setAllSalesOrders] = useState([]);
  const [soDialogOpen, setSoDialogOpen] = useState(false);
  const [editingSO, setEditingSO] = useState(null);
  const [soForm, setSoForm] = useState({ customer_firm_id: '', date: getToday(), line_items: [{ ...emptyLineItem }], remarks: '', round_off: '', credit_days: '' });
  const [soError, setSoError] = useState('');
  const [soSubmitting, setSoSubmitting] = useState(false);
  const [poEditRoundOff, setPoEditRoundOff] = useState(false);
  const currentMonth = getDateRange('current-month');
  const [soDateRange, setSoDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [soQuick, setSoQuick] = useState('current-month');

  // Sales Entry state
  const [salesEntries, setSalesEntries] = useState([]);
  const [seDialogOpen, setSeDialogOpen] = useState(false);
  const [seForm, setSeForm] = useState({ sales_order_id: '', customer_firm_id: '', date: getToday(), line_items: [{ ...emptyLineItem }], invoice_number: '', vehicle_number: '', remarks: '', round_off: '', credit_days: '' });
  const [seError, setSeError] = useState('');
  const [seSubmitting, setSeSubmitting] = useState(false);
  const [peEditRoundOff, setPeEditRoundOff] = useState(false);
  const [seDateRange, setSeDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [seQuick, setPeQuick] = useState('current-month');
  const [selectedSOForEntry, setSelectedSOForEntry] = useState(null);

  // Outstanding state
  const [outstanding, setOutstanding] = useState(null);
  const [outstandingLoading, setOutstandingLoading] = useState(false);

  // Receipt outstanding state
  const [paymentOutstanding, setReceiptOutstanding] = useState(null);
  const [paymentOutstandingLoading, setReceiptOutstandingLoading] = useState(false);

  // Unified payment state
  const [receipts, setReceipts] = useState([]);
  const [paymentsLoading, setReceiptsLoading] = useState(false);
  const [selectedPayIds, setSelectedPayIds] = useState([]);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptForm, setReceiptForm] = useState({ customer_firm_id: '', amount: '', date: getToday(), mode: 'online', reference: '', bank_name: '', remarks: '', allocations: [] });
  const [receiptDateRange, setReceiptDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [receiptQuick, setReceiptQuick] = useState('current-month');
  const [receiptError, setReceiptError] = useState('');
  const [receiptSubmitting, setReceiptSubmitting] = useState(false);

  // Clear cheque dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearingReceipt, setClearingReceipt] = useState(null);
  const [clearDate, setClearDate] = useState(getToday());
  const [clearSubmitting, setClearSubmitting] = useState(false);

  // Selection state
  const [selectedSoIds, setSelectedSoIds] = useState([]);
  const [selectedSeIds, setSelectedSeIds] = useState([]);
  const [selectedFirmIds, setSelectedFirmIds] = useState([]);

  // Customer firm state
  const [editingFirmId, setEditingFirmId] = useState(null);
  const [editFirmName, setEditFirmName] = useState('');
  const [editFirmCreditDays, setEditFirmCreditDays] = useState('');

  const [firmDialogOpen, setFirmDialogOpen] = useState(false);
  const [editingFirm, setEditingFirm] = useState(null);
  const [firmForm, setFirmForm] = useState({ group_id: '', company_name: '', company_email: '', contact_persons: [{ ...emptyContact }], credit_days: '30', gstin: '', state: '', state_code: '' });
  const [firmError, setFirmError] = useState('');
  const [firmSubmitting, setFirmSubmitting] = useState(false);

  // Quick product creation state
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productForm, setProductForm] = useState({ name: '', category: '', group: '', kg_per_bag: '', brands: '', hsn_code: '', default_gst_rate: '', description: '' });
  const [productSubmitting, setProductSubmitting] = useState(false);
  const [productError, setProductError] = useState('');

  // GST data
  const [hsnCodes, setHsnCodes] = useState([]);
  const [companySettings, setCompanySettings] = useState({ state: '', state_code: '' });

  // Quick customer creation state (from PE form)
  const [quickFirmDialogOpen, setQuickFirmDialogOpen] = useState(false);
  const [quickFirmForm, setQuickFirmForm] = useState({ group_id: '', company_name: '', company_email: '', credit_days: 30, contact_persons: [] });
  const [quickFirmSubmitting, setQuickFirmSubmitting] = useState(false);
  const [quickFirmError, setQuickFirmError] = useState('');

  const [expandedMobileId, setExpandedMobileId] = useState(null);

  const [error, setError] = useState('');

  // Sorting hooks
  const { sortedData: sortedSOs, sortKey: soSortKey, sortDirection: soSortDir, handleSort: handleSoSort } = useSortableTable(salesOrders, 'so_number', 'asc');
  const { sortedData: sortedPEs, sortKey: peSortKey, sortDirection: peSortDir, handleSort: handlePeSort } = useSortableTable(salesEntries, 'date', 'desc');
  const { sortedData: sortedFirms, sortKey: firmSortKey, sortDirection: firmSortDir, handleSort: handleFirmSort } = useSortableTable(customerFirms, 'company_name', 'asc');
  const [submitting, setSubmitting] = useState(false);

  usePageCallbacks({
    onNewEntry: () => {
      if (activeTab === 'sales-orders') { resetSoForm(); setSoDialogOpen(true); }
      else if (activeTab === 'sales-entries') { resetSeForm(); setSeDialogOpen(true); }
      else if (activeTab === 'customers') { openAddFirm(); }
    },
    onPrevTab: () => switchTab('prev'),
    onNextTab: () => switchTab('next'),
    onDateQuick: (key) => {
      const range = getDateRange(key);
      if (activeTab === 'sales-orders') {
        setSoDateRange({ start_date: range.start_date, end_date: range.end_date });
        setSoQuick(key);
        fetchSalesOrders(range.start_date, range.end_date);
      } else if (activeTab === 'sales-entries') {
        setSeDateRange({ start_date: range.start_date, end_date: range.end_date });
        setPeQuick(key);
        fetchSalesEntries(range.start_date, range.end_date);
      }
    },
  });

  // Alt+P = New Product (when SO dialog is open)
  // Alt+S = New Customer (when PE dialog is open)
  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.code === 'KeyP' && soDialogOpen && !productDialogOpen) {
        e.preventDefault();
        openNewProduct();
      }
      if (e.altKey && e.code === 'KeyS' && seDialogOpen && !quickFirmDialogOpen) {
        e.preventDefault();
        openQuickFirm();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [soDialogOpen, productDialogOpen, seDialogOpen, quickFirmDialogOpen]); // eslint-disable-line

  const [dataLoaded, setDataLoaded] = useState(false);
  useEffect(() => { if (activeTab && !dataLoaded) { fetchInitialData(); setDataLoaded(true); } }, [activeTab]); // eslint-disable-line

  const fetchInitialData = async () => {
    try {
      const [groupsRes, productsRes, hsnRes, settingsRes] = await Promise.all([
        getCustomerFirms(),
        getProducts(),
        getHSNCodes(),
        getCompanySettings()
      ]);
      setCustomerFirms(groupsRes.data);
      setProducts(productsRes.data);
      setHsnCodes(hsnRes.data);
      setCompanySettings(settingsRes.data);
      fetchSalesOrders(soDateRange.start_date, soDateRange.end_date);
      fetchAllSalesOrders();
    } catch (e) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  // ========== PURCHASE ORDERS ==========
  const fetchSalesOrders = async (start, end) => {
    try {
      const res = await getSalesOrders(null, start, end);
      setSalesOrders(res.data);
    } catch (e) { toast.error('Failed to fetch sales orders'); }
  };

  const fetchAllSalesOrders = async () => {
    try {
      const res = await getSalesOrders();
      setAllSalesOrders(res.data);
    } catch (err) { console.error('Failed to fetch POs:', err); }
  };

  const resetSoForm = () => {
    setSoForm({ customer_firm_id: '', date: getToday(), line_items: [{ ...emptyLineItem }], remarks: '', round_off: '', credit_days: '' });
    setEditingSO(null); setSoError('');
  };

  // GST calculation helper
  const calcGST = (taxableAmount, gstRate, taxType) => {
    const gst = (parseFloat(gstRate) || 0) * (parseFloat(taxableAmount) || 0) / 100;
    if (taxType === 'inter') return { cgst_amount: 0, sgst_amount: 0, igst_amount: Math.round(gst * 100) / 100 };
    const half = Math.round(gst / 2 * 100) / 100;
    return { cgst_amount: half, sgst_amount: half, igst_amount: 0 };
  };

  // Determine tax type based on customer state vs company state
  const getTaxType = (customerFirmId) => {
    if (!companySettings.state) return '';
    const firm = customerFirms.find(f => f.id === customerFirmId);
    if (!firm || !firm.state) return '';
    return firm.state === companySettings.state ? 'intra' : 'inter';
  };

  // Update a specific line item field
  const updateLineItem = (form, setForm, index, field, value) => {
    setForm(prev => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, line_items: items };
    });
  };

  // Add a new line item
  const addLineItem = (setForm) => {
    setForm(prev => ({ ...prev, line_items: [...prev.line_items, { ...emptyLineItem }] }));
  };

  // Remove a line item
  const removeLineItem = (setForm, index) => {
    setForm(prev => {
      const items = prev.line_items.filter((_, i) => i !== index);
      return { ...prev, line_items: items.length ? items : [{ ...emptyLineItem }] };
    });
  };

  // Auto-fill GST for a line item when product changes
  const autoFillLineItemGST = async (form, setForm, index, productId) => {
    const prod = products.find(p => p.id === productId);
    if (!prod) return;
    const taxType = getTaxType(form.customer_firm_id);
    const date = form.date || getToday();
    let gstRate = prod.default_gst_rate || 0;
    if (prod.hsn_code) {
      try {
        const res = await getHSNRateForDate(prod.hsn_code, date);
        gstRate = res.data.gst_rate;
      } catch (err) {
        console.error('HSN rate lookup failed:', err);
        const hsn = hsnCodes.find(h => h.hsn_code === prod.hsn_code);
        if (hsn) gstRate = hsn.gst_rate;
      }
    }
    setForm(prev => {
      const items = [...prev.line_items];
      const li = items[index];
      const taxable = (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
      const { cgst_amount, sgst_amount, igst_amount } = calcGST(taxable, gstRate, taxType);
      items[index] = {
        ...li, product_id: productId, hsn_code: prod.hsn_code || '', description: prod.description || '',
        gst_rate: String(gstRate), tax_type: taxType,
        cgst_amount: String(cgst_amount), sgst_amount: String(sgst_amount), igst_amount: String(igst_amount)
      };
      return { ...prev, line_items: items };
    });
  };

  // Auto-fill line item from last SO for this product+customer (PO form only)
  const autoFillLineItemFromLastSO = async (form, setForm, index, productId) => {
    if (!form.customer_firm_id || !productId) return;
    try {
      const res = await getLastSalesOrder(form.customer_firm_id, productId);
      const lastLi = res.data?.line_items?.find(li => li.product_id === productId);
      if (lastLi) {
        setForm(prev => {
          const items = [...prev.line_items];
          const li = items[index];
          const qty = lastLi.quantity_kg ? parseFloat(lastLi.quantity_kg) : 0;
          const rate = lastLi.rate ? parseFloat(lastLi.rate) : 0;
          const taxable = qty * rate;
          const gstRate = li.gst_rate || lastLi.gst_rate || 0;
          const taxType = li.tax_type || lastLi.tax_type || '';
          const { cgst_amount, sgst_amount, igst_amount } = calcGST(taxable, gstRate, taxType);
          items[index] = {
            ...li,
            quantity_kg: lastLi.quantity_kg ? String(lastLi.quantity_kg) : '',
            quantity_bags: lastLi.quantity_bags != null ? String(lastLi.quantity_bags) : '',
            rate: lastLi.rate ? String(lastLi.rate) : '',
            brand: lastLi.brand || '',
            cgst_amount: String(cgst_amount), sgst_amount: String(sgst_amount), igst_amount: String(igst_amount)
          };
          return { ...prev, line_items: items };
        });
      } else {
        setForm(prev => {
          const items = [...prev.line_items];
          items[index] = { ...items[index], quantity_kg: '', quantity_bags: '', rate: '', brand: '', cgst_amount: '', sgst_amount: '', igst_amount: '' };
          return { ...prev, line_items: items };
        });
      }
    } catch (err) {
      console.error('SO remaining fetch failed:', err);
      setForm(prev => {
        const items = [...prev.line_items];
        items[index] = { ...items[index], quantity_kg: '', quantity_bags: '', rate: '', brand: '', cgst_amount: '', sgst_amount: '', igst_amount: '' };
        return { ...prev, line_items: items };
      });
    }
  };

  // Recalculate GST for a line item when qty/rate/gst_rate changes
  const recalcLineItemGST = (form, setForm, index) => {
    setForm(prev => {
      const items = [...prev.line_items];
      const li = items[index];
      const taxable = (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
      const { cgst_amount, sgst_amount, igst_amount } = calcGST(taxable, li.gst_rate, li.tax_type);
      items[index] = { ...li, cgst_amount: String(cgst_amount), sgst_amount: String(sgst_amount), igst_amount: String(igst_amount) };
      return { ...prev, line_items: items };
    });
  };

  // Re-lookup HSN rate when date changes for all line items
  const reLookupAllHSNRates = async (form, setForm) => {
    if (!form.date) return;
    const taxType = getTaxType(form.customer_firm_id);
    setForm(prev => {
      const items = prev.line_items.map(li => {
        if (!li.hsn_code) return li;
        return { ...li, tax_type: taxType };
      });
      return { ...prev, line_items: items };
    });
    for (let i = 0; i < form.line_items.length; i++) {
      const li = form.line_items[i];
      if (!li.hsn_code) continue;
      try {
        const res = await getHSNRateForDate(li.hsn_code, form.date);
        const gstRate = res.data.gst_rate;
        setForm(prev => {
          const items = [...prev.line_items];
          const item = items[i];
          const taxable = (parseFloat(item.quantity_kg) || 0) * (parseFloat(item.rate) || 0);
          const { cgst_amount, sgst_amount, igst_amount } = calcGST(taxable, gstRate, taxType);
          items[i] = { ...item, gst_rate: String(gstRate), tax_type: taxType, cgst_amount: String(cgst_amount), sgst_amount: String(sgst_amount), igst_amount: String(igst_amount) };
          return { ...prev, line_items: items };
        });
      } catch (err) { console.error('GST rate refresh failed:', err); }
    }
  };

  // Handle line item kg change with auto bags calculation
  const handleLineItemKgChange = (form, setForm, index, val) => {
    setForm(prev => {
      const items = [...prev.line_items];
      const li = items[index];
      const product = products.find(p => p.id === li.product_id);
      const bags = product && product.kg_per_bag ? Math.round(parseFloat(val) / product.kg_per_bag) : li.quantity_bags;
      items[index] = { ...li, quantity_kg: val, quantity_bags: bags };
      return { ...prev, line_items: items };
    });
    setTimeout(() => recalcLineItemGST(form, setForm, index), 0);
  };

  // Compute totals from line items
  const computeTotals = (lineItems, roundOff) => {
    let subtotal = 0, totalTax = 0;
    lineItems.forEach(li => {
      subtotal += (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
      totalTax += (parseFloat(li.cgst_amount) || 0) + (parseFloat(li.sgst_amount) || 0) + (parseFloat(li.igst_amount) || 0);
    });
    const grand = subtotal + totalTax + (parseFloat(roundOff) || 0);
    return { subtotal, totalTax, grand };
  };

  const handleSOCustomerChange = async (firmId) => {
    const firm = customerFirms.find(f => f.id === firmId);
    setSoForm(prev => ({ ...prev, customer_firm_id: firmId, credit_days: firm ? String(firm.credit_days || 30) : prev.credit_days }));
    if (firmId && !editingSO) {
      try {
        const res = await getLastSalesOrder(firmId);
        if (res.data && res.data.line_items && res.data.line_items.length > 0) {
          const taxType = getTaxType(firmId);
          const lastItems = res.data.line_items.map(li => {
            const taxable = (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
            const gstRate = li.gst_rate || 0;
            const { cgst_amount, sgst_amount, igst_amount } = calcGST(taxable, gstRate, taxType || li.tax_type || '');
            return {
              product_id: li.product_id || '', description: li.description || '', hsn_code: li.hsn_code || '',
              quantity_kg: li.quantity_kg ? String(li.quantity_kg) : '', quantity_bags: li.quantity_bags != null ? String(li.quantity_bags) : '',
              rate: li.rate ? String(li.rate) : '', brand: li.brand || '',
              gst_rate: gstRate ? String(gstRate) : '', tax_type: taxType || li.tax_type || '',
              cgst_amount: String(cgst_amount), sgst_amount: String(sgst_amount), igst_amount: String(igst_amount)
            };
          });
          setSoForm(prev => ({
            ...prev, customer_firm_id: firmId,
            line_items: lastItems,
            remarks: prev.remarks || (res.data.remarks || ''),
          }));
          if (!res.data.remarks) {
            try {
              const anyRes = await getLastSalesOrder('');
              if (anyRes.data?.remarks) setSoForm(prev => ({ ...prev, remarks: prev.remarks || anyRes.data.remarks }));
            } catch (err) { console.error('Fallback remarks fetch failed:', err); }
          }
        }
      } catch (err) { console.error('Customer firm data fetch failed:', err); }
    }
  };

  const handleCreateSO = async (e) => {
    e.preventDefault(); setSoError(''); setSoSubmitting(true);
    if (!isDateInFY(soForm.date, selectedFY)) { setSoError(getFYDateError(selectedFY, fyLabel)); setSoSubmitting(false); return; }
    const validItems = soForm.line_items.filter(li => li.product_id && li.quantity_kg && li.rate);
    if (!validItems.length) { setSoError('At least one complete line item is required'); setSoSubmitting(false); return; }
    try {
      const payload = {
        customer_firm_id: soForm.customer_firm_id,
        date: soForm.date,
        line_items: validItems.map(li => ({
          product_id: li.product_id,
          description: li.description || '',
          hsn_code: li.hsn_code || '',
          quantity_kg: parseFloat(li.quantity_kg),
          quantity_bags: li.quantity_bags ? parseFloat(li.quantity_bags) : null,
          rate: parseFloat(li.rate),
          brand: li.brand || '',
          gst_rate: parseFloat(li.gst_rate) || 0,
          cgst_amount: parseFloat(li.cgst_amount) || 0,
          sgst_amount: parseFloat(li.sgst_amount) || 0,
          igst_amount: parseFloat(li.igst_amount) || 0,
          tax_type: li.tax_type || '',
        })),
        remarks: soForm.remarks,
        round_off: parseFloat(soForm.round_off) || 0,
        credit_days: parseInt(soForm.credit_days) || 0
      };
      if (editingSO) {
        await updateSalesOrder(editingSO.id, payload);
        toast.success('Sales order updated');
      } else {
        await createSalesOrder(payload);
        toast.success('Sales order created');
      }
      setSoDialogOpen(false); resetSoForm();
      fetchSalesOrders(soDateRange.start_date, soDateRange.end_date);
      fetchAllSalesOrders();
    } catch (err) { setSoError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSoSubmitting(false); }
  };

  const openEditSO = (po) => {
    setEditingSO(po);
    const lineItems = (po.line_items || []).map(li => ({
      product_id: li.product_id || '', description: li.description || '', hsn_code: li.hsn_code || '',
      quantity_kg: String(li.quantity_kg || ''), quantity_bags: li.quantity_bags != null ? String(li.quantity_bags) : '',
      rate: String(li.rate || ''), brand: li.brand || '',
      gst_rate: String(li.gst_rate || ''), cgst_amount: String(li.cgst_amount || ''),
      sgst_amount: String(li.sgst_amount || ''), igst_amount: String(li.igst_amount || ''),
      tax_type: li.tax_type || ''
    }));
    setSoForm({
      customer_firm_id: po.customer_firm_id, date: po.date,
      line_items: lineItems.length ? lineItems : [{ ...emptyLineItem }],
      remarks: po.remarks || '', round_off: String(po.round_off || '')
    });
    setSoDialogOpen(true);
  };

  const handleDeleteSO = async (po) => {
    if (!window.confirm('Delete this sales order?')) return;
    try { await deleteSalesOrder(po.id); toast.success('Deleted'); fetchSalesOrders(soDateRange.start_date, soDateRange.end_date); fetchAllSalesOrders(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // ========== PURCHASE ENTRIES ==========
  const fetchSalesEntries = async (start, end) => {
    try {
      const res = await getSalesEntries(null, null, start, end);
      setSalesEntries(res.data);
    } catch (e) { toast.error('Failed to fetch entries'); }
  };

  const resetSeForm = () => {
    setSeForm({ sales_order_id: '', customer_firm_id: '', date: getToday(), line_items: [{ ...emptyLineItem }], invoice_number: '', vehicle_number: '', remarks: '', round_off: '', credit_days: '' });
    setSeError(''); setSelectedSOForEntry(null);
  };

  const handlePePoChange = async (poId) => {
    setSeForm(prev => ({ ...prev, sales_order_id: poId }));
    if (poId) {
      const po = allSalesOrders.find(o => o.id === poId);
      if (po) {
        setSelectedSOForEntry(po);
        // Pre-fill line items from SO's line items
        const poItems = (po.line_items || []).map(li => ({
          product_id: li.product_id || '', description: li.description || '', hsn_code: li.hsn_code || '',
          quantity_kg: '', quantity_bags: '', rate: String(li.rate || ''), brand: li.brand || '',
          gst_rate: String(li.gst_rate || ''), cgst_amount: '', sgst_amount: '', igst_amount: '',
          tax_type: li.tax_type || ''
        }));
        setSeForm(prev => ({
          ...prev, sales_order_id: poId,
          line_items: poItems.length ? poItems : [{ ...emptyLineItem }]
        }));
      }
    }
  };

  const handleDeleteSE = async (entry) => {
    if (!window.confirm('Delete this sales entry?')) return;
    try {
      await deleteSalesEntry(entry.id); toast.success('Deleted');
      fetchSalesEntries(seDateRange.start_date, seDateRange.end_date);
      fetchSalesOrders(soDateRange.start_date, soDateRange.end_date);
      fetchAllSalesOrders();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [editingSE, setEditingSE] = useState(null);

  const openEditSE = async (entry) => {
    setEditingSE(entry);
    const lineItems = (entry.line_items || []).map(li => ({
      product_id: li.product_id || '', description: li.description || '', hsn_code: li.hsn_code || '',
      quantity_kg: String(li.quantity_kg || ''), quantity_bags: li.quantity_bags != null ? String(li.quantity_bags) : '',
      rate: String(li.rate || ''), brand: li.brand || '',
      gst_rate: String(li.gst_rate || ''), cgst_amount: String(li.cgst_amount || ''),
      sgst_amount: String(li.sgst_amount || ''), igst_amount: String(li.igst_amount || ''),
      tax_type: li.tax_type || ''
    }));
    setSeForm({
      sales_order_id: entry.sales_order_id, customer_firm_id: entry.customer_firm_id,
      date: entry.date,
      line_items: lineItems.length ? lineItems : [{ ...emptyLineItem }],
      invoice_number: entry.invoice_number || '', vehicle_number: entry.vehicle_number || '',
      remarks: entry.remarks || '', round_off: String(entry.round_off || ''),
      credit_days: entry.credit_days != null ? String(entry.credit_days) : ''
    });
    const po = allSalesOrders.find(o => o.id === entry.sales_order_id);
    if (po) {
      setSelectedSOForEntry(po);
    }
    setSeError(''); setSeDialogOpen(true);
  };

  const handleSubmitSE = async (e) => {
    e.preventDefault(); setSeError(''); setSeSubmitting(true);
    if (!isDateInFY(seForm.date, selectedFY)) { setSeError(getFYDateError(selectedFY, fyLabel)); setSeSubmitting(false); return; }
    const validItems = seForm.line_items.filter(li => li.product_id && li.quantity_kg && li.rate);
    if (!validItems.length) { setSeError('At least one complete line item is required'); setSeSubmitting(false); return; }
    try {
      const payload = {
        sales_order_id: seForm.sales_order_id, customer_firm_id: seForm.customer_firm_id,
        date: seForm.date,
        line_items: validItems.map(li => ({
          product_id: li.product_id,
          description: li.description || '',
          hsn_code: li.hsn_code || '',
          quantity_kg: parseFloat(li.quantity_kg),
          quantity_bags: li.quantity_bags ? parseFloat(li.quantity_bags) : null,
          rate: parseFloat(li.rate),
          brand: li.brand || '',
          gst_rate: parseFloat(li.gst_rate) || 0,
          cgst_amount: parseFloat(li.cgst_amount) || 0,
          sgst_amount: parseFloat(li.sgst_amount) || 0,
          igst_amount: parseFloat(li.igst_amount) || 0,
          tax_type: li.tax_type || '',
        })),
        invoice_number: seForm.invoice_number, vehicle_number: seForm.vehicle_number,
        remarks: seForm.remarks, round_off: parseFloat(seForm.round_off) || 0,
        credit_days: parseInt(seForm.credit_days) || 0,
      };
      if (editingSE) {
        await updateSalesEntry(editingSE.id, payload);
        toast.success('Sales entry updated');
      } else {
        await createSalesEntry(payload);
        toast.success('Sales entry created');
      }
      setSeDialogOpen(false); resetSeForm(); setEditingSE(null);
      fetchSalesEntries(seDateRange.start_date, seDateRange.end_date);
      fetchSalesOrders(soDateRange.start_date, soDateRange.end_date);
      fetchAllSalesOrders();
    } catch (err) { setSeError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSeSubmitting(false); }
  };

  // ========== BULK OPERATIONS ==========
  const togglePoSelect = (id) => setSelectedSoIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const togglePeSelect = (id) => setSelectedSeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleFirmSelect = (id) => setSelectedFirmIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allSoSelected = salesOrders.length > 0 && salesOrders.every(po => selectedSoIds.includes(po.id));
  const allPeSelected = salesEntries.length > 0 && salesEntries.every(pe => selectedSeIds.includes(pe.id));
  const customerFirmsSelected = customerFirms.length > 0 && customerFirms.every(f => selectedFirmIds.includes(f.id));
  const toggleAllSo = () => { if (allSoSelected) setSelectedSoIds([]); else setSelectedSoIds(sortedSOs.map(po => po.id)); };
  const toggleAllPe = () => { if (allPeSelected) setSelectedSeIds([]); else setSelectedSeIds(sortedPEs.map(pe => pe.id)); };
  const toggleAllFirms = () => { if (customerFirmsSelected) setSelectedFirmIds([]); else setSelectedFirmIds(sortedFirms.map(f => f.id)); };

  const handleBulkDeletePOs = async () => {
    if (!window.confirm(`Delete ${selectedSoIds.length} sales orders?`)) return;
    try {
      const res = await bulkDeleteSalesOrders(selectedSoIds);
      const msg = res.data?.message || 'Deleted';
      const skippedLen = Array.isArray(res.data?.skipped) ? res.data.skipped.length : (res.data?.skipped || 0);
      if (skippedLen > 0) toast.warning(msg);
      else toast.success(msg);
      setSelectedSoIds([]);
      fetchSalesOrders(soDateRange.start_date, soDateRange.end_date);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const handleBulkDeletePEs = async () => {
    if (!window.confirm(`Delete ${selectedSeIds.length} sales entries?`)) return;
    try {
      const res = await bulkDeleteSalesEntries(selectedSeIds);
      const msg = res.data?.message || 'Deleted';
      const skippedLen = Array.isArray(res.data?.skipped) ? res.data.skipped.length : (res.data?.skipped || 0);
      if (skippedLen > 0) toast.warning(msg);
      else toast.success(msg);
      setSelectedSeIds([]);
      fetchSalesEntries(seDateRange.start_date, seDateRange.end_date);
      fetchSalesOrders(soDateRange.start_date, soDateRange.end_date);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const handleBulkDeleteFirms = async () => {
    if (!window.confirm(`Delete ${selectedFirmIds.length} customers?`)) return;
    try {
      const res = await bulkDeleteCustomerFirms(selectedFirmIds);
      const msg = res.data?.message || 'Deleted';
      const skippedLen = Array.isArray(res.data?.skipped) ? res.data.skipped.length : (res.data?.skipped || 0);
      if (skippedLen > 0) toast.warning(msg);
      else toast.success(msg);
      setSelectedFirmIds([]);
      fetchAllCustomerFirms();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // ========== OUTSTANDING ==========
  const fetchOutstanding = async () => {
    setOutstandingLoading(true);
    try { const res = await getSOOutstanding(); setOutstanding(res.data); }
    catch (e) { toast.error('Failed to load outstanding'); }
    finally { setOutstandingLoading(false); }
  };

  // ========== PAYMENT OUTSTANDING ==========
  const fetchReceiptOutstanding = async () => {
    setReceiptOutstandingLoading(true);
    try { const res = await getSalesPaymentOutstanding(); setReceiptOutstanding(res.data); }
    catch (e) { toast.error('Failed to load payment outstanding'); }
    finally { setReceiptOutstandingLoading(false); }
  };

  // ========== UNIFIED PAYMENTS ==========
  const fetchReceipts = async (start, end) => {
    setReceiptsLoading(true);
    const s = start ?? receiptDateRange.start_date;
    const e = end ?? receiptDateRange.end_date;
    try { const res = await getSaleReceipts(undefined, undefined, s, e); setReceipts(res.data); }
    catch (err) { toast.error('Failed to load receipts'); }
    finally { setReceiptsLoading(false); }
  };
  // Get firm options from outstanding data (with credit days)
  const payFirmOptions = (paymentOutstanding?.firms || []).map(f => {
    const customer = customers.find(s => s.id === f.customer_firm_id) || {};
    return { value: f.customer_firm_id, label: `${f.customer_firm_name} (${customer.credit_days || 0}d credit)` };
  });
  const modeOptions = [
    { value: 'online', label: 'Online / NEFT / RTGS' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
  ];
  const openReceiptDialog = () => {
    setReceiptForm({ customer_firm_id: '', amount: '', date: getToday(), mode: 'online', reference: '', bank_name: '', remarks: '', allocations: [] });
    setReceiptError('');
    setReceiptDialogOpen(true);
  };
  const selectPayFirm = (customerId) => {
    const firm = (paymentOutstanding?.firms || []).find(f => f.customer_firm_id === customerId);
    if (!firm) { setReceiptForm(prev => ({ ...prev, customer_firm_id: customerId, allocations: [] })); return; }
    const allocs = (firm.entries || []).map(e => ({
      sales_entry_id: e.id, invoice_number: e.invoice_number || '-',
      outstanding: e.outstanding, due_date: e.due_date, amount: ''
    }));
    setReceiptForm(prev => ({ ...prev, customer_firm_id: customerId, amount: '', allocations: allocs }));
  };
  const updatePayAmount = (value) => {
    const amt = parseFloat(value) || 0;
    let remaining = amt;
    setReceiptForm(prev => {
      const allocs = prev.allocations.map(a => {
        if (remaining <= 0) return { ...a, amount: '' };
        const alloc = Math.min(remaining, a.outstanding);
        remaining -= alloc;
        return { ...a, amount: String(alloc) };
      });
      return { ...prev, amount: value, allocations: allocs };
    });
  };
  const updateAllocation = (idx, value) => {
    setReceiptForm(prev => {
      const allocs = [...prev.allocations];
      allocs[idx] = { ...allocs[idx], amount: value };
      const total = allocs.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
      return { ...prev, allocations: allocs, amount: String(total) };
    });
  };
  const handlePaySubmit = async (e) => {
    e.preventDefault();
    setReceiptError('');
    setReceiptSubmitting(true);
    if (!receiptForm.customer_firm_id) { setReceiptError('Select a customer'); setReceiptSubmitting(false); return; }
    const amt = parseFloat(receiptForm.amount);
    if (!amt || amt <= 0) { setReceiptError('Enter a valid amount'); setReceiptSubmitting(false); return; }
    if (receiptForm.mode === 'cheque' && !receiptForm.reference.trim()) { setReceiptError('Cheque number is required'); setReceiptSubmitting(false); return; }
    const validAllocs = receiptForm.allocations.filter(a => parseFloat(a.amount) > 0).map(a => ({ sales_entry_id: a.sales_entry_id, amount: parseFloat(a.amount) }));
    if (validAllocs.length === 0) { setReceiptError('Allocate amount to at least one invoice'); setReceiptSubmitting(false); return; }
    const allocTotal = validAllocs.reduce((s, a) => s + a.amount, 0);
    if (Math.abs(allocTotal - amt) > 0.01) { setReceiptError(`Allocation (${formatCurrency(allocTotal)}) doesn't match amount (${formatCurrency(amt)})`); setReceiptSubmitting(false); return; }
    try {
      await createSaleReceipt({
        customer_firm_id: receiptForm.customer_firm_id, amount: amt, date: receiptForm.date,
        mode: receiptForm.mode, reference: receiptForm.reference, bank_name: receiptForm.bank_name,
        remarks: receiptForm.remarks, allocations: validAllocs
      });
      toast.success(receiptForm.mode === 'cheque' ? 'Cheque recorded (pending clearance)' : 'Receipt recorded');
      setReceiptDialogOpen(false);
      fetchReceiptOutstanding();
      if (activeTab === 'receipts') fetchReceipts();
    } catch (err) { setReceiptError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setReceiptSubmitting(false); }
  };
  const handleClearReceipt = async () => {
    if (!clearingReceipt) return;
    setClearSubmitting(true);
    try {
      await clearSaleReceipt(clearingReceipt.id, { clear_date: clearDate });
      toast.success('Cheque cleared & payment applied');
      setClearDialogOpen(false);
      setClearingReceipt(null);
      fetchReceipts();
      fetchReceiptOutstanding();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setClearSubmitting(false); }
  };
  const handleBounceReceipt = async (p) => {
    if (!window.confirm(`Mark cheque #${p.reference} as bounced?`)) return;
    try { await bounceSaleReceipt(p.id); toast.success('Receipt marked as bounced'); fetchReceipts(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const handleDeleteReceipt = async (p) => {
    if (!window.confirm(`Delete payment?`)) return;
    try { await deleteSaleReceipt(p.id); toast.success('Receipt deleted'); fetchReceipts(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const allPaySelected = receipts.length > 0 && receipts.filter(p => p.status === 'pending' || p.status === 'bounced').every(p => selectedPayIds.includes(p.id));
  const togglePaySelect = (id) => setSelectedPayIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllPay = () => {
    const deletable = receipts.filter(p => p.status === 'pending' || p.status === 'bounced');
    if (allPaySelected) setSelectedPayIds([]); else setSelectedPayIds(deletable.map(p => p.id));
  };
  const handleBulkDeleteReceipts = async () => {
    if (!window.confirm(`Delete ${selectedPayIds.length} selected receipts?`)) return;
    let deleted = 0;
    for (const id of selectedPayIds) {
      try { await deleteSaleReceipt(id); deleted++; } catch (err) { console.error(`Failed to delete payment ${id}:`, err); }
    }
    toast.success(`Deleted ${deleted} receipts`);
    setSelectedPayIds([]);
    fetchReceipts();
  };

  // ========== SUPPLIERS ==========
  const fetchAllCustomerFirms = async () => {
    try {
      const [firmsRes, groupsRes] = await Promise.all([getCustomerFirms(), getCustomerFirms()]);
      setCustomerFirms(firmsRes.data);
      setCustomerFirms(groupsRes.data);
    } catch (e) { toast.error('Failed to load customers'); }
  };

  const openAddFirm = () => {
    setEditingFirm(null);
    setFirmForm({ group_id: '', company_name: '', company_email: '', contact_persons: [{ ...emptyContact }], credit_days: '30', gstin: '', state: '', state_code: '' });
    setFirmError(''); setFirmDialogOpen(true);
  };

  const openEditFirm = (firm) => {
    setEditingFirm(firm);
    setFirmForm({
      group_id: firm.group_id, company_name: firm.company_name, company_email: firm.company_email || '',
      contact_persons: firm.contact_persons?.length ? firm.contact_persons : [{ ...emptyContact }],
      credit_days: firm.credit_days?.toString() || '30',
      gstin: firm.gstin || '', state: firm.state || '', state_code: firm.state_code || ''
    });
    setFirmError(''); setFirmDialogOpen(true);
  };

  const handleCreateFirmFromSelect = async (name, context) => {
    try {
      const res = await createCustomerFirm({ company_name: name, credit_days: 30 });
      toast.success('Customer firm created');
      const firmsRes = await getCustomerFirms();
      setCustomerFirms(firmsRes.data);
      if (context === 'so') {
        setSoForm(p => ({ ...p, customer_firm_id: res.data.id }));
      } else if (context === 'quickfirm') {
        setQuickFirmForm(p => ({ ...p, group_id: res.data.id }));
      } else {
        setFirmForm(p => ({ ...p, group_id: res.data.id }));
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleSaveGroupEdit = async (groupId) => {
    if (!editFirmName.trim()) return;
    try {
      await updateCustomerFirm(groupId, { company_name: editFirmName.trim(), credit_days: parseInt(editFirmCreditDays) || 30 });
      toast.success('Firm updated');
      setEditingFirmId(null);
      const firmsRes = await getCustomerFirms();
      setCustomerFirms(firmsRes.data);
      fetchAllCustomerFirms();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete "${group.company_name || group.name}"?`)) return;
    try {
      await deleteCustomerFirm(group.id);
      toast.success('Group deleted');
      const groupsRes = await getCustomerFirms();
      setCustomerFirms(groupsRes.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openNewProduct = () => {
    setProductForm({ name: '', category: '', group: '', kg_per_bag: '', brands: '', hsn_code: '', default_gst_rate: '', description: '' });
    setProductError('');
    setProductDialogOpen(true);
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setProductError(''); setProductSubmitting(true);
    try {
      const payload = {
        name: productForm.name,
        category: productForm.category,
        group: productForm.group,
        kg_per_bag: productForm.kg_per_bag ? parseFloat(productForm.kg_per_bag) : 50.0,
        brands: productForm.brands ? productForm.brands.split(',').map(b => b.trim()).filter(Boolean) : [],
        hsn_code: productForm.hsn_code || '',
        default_gst_rate: productForm.default_gst_rate ? parseFloat(productForm.default_gst_rate) : 0,
        description: productForm.description || ''
      };
      const res = await createProduct(payload);
      toast.success('Product created');
      setProductDialogOpen(false);
      const productsRes = await getProducts();
      setProducts(productsRes.data);
      setSoForm(p => ({ ...p, product_id: res.data.id }));
    } catch (err) { setProductError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setProductSubmitting(false); }
  };

  const openQuickFirm = () => {
    // Pre-select the group from the selected PO's customer_firm_id
    const groupId = selectedSOForEntry?.customer_firm_id || '';
    setQuickFirmForm({ group_id: groupId, company_name: '', company_email: '', credit_days: 30, contact_persons: [] });
    setQuickFirmError('');
    // Auto-fetch contacts from existing firms in same group (check both customers and customerFirms)
    if (groupId) {
      const existingFirm = customers.find(f => f.group_id === groupId) || customerFirms.find(f => f.group_id === groupId);
      if (existingFirm && existingFirm.contact_persons?.length > 0) {
        setQuickFirmForm(p => ({ ...p, contact_persons: existingFirm.contact_persons.map(cp => ({ ...cp })) }));
      }
    }
    setQuickFirmDialogOpen(true);
  };

  const handleQuickFirmGroupChange = (groupId) => {
    setQuickFirmForm(p => ({ ...p, group_id: groupId }));
    const existingFirm = customers.find(f => f.group_id === groupId) || customerFirms.find(f => f.group_id === groupId);
    if (existingFirm && existingFirm.contact_persons?.length > 0) {
      setQuickFirmForm(p => ({ ...p, contact_persons: existingFirm.contact_persons.map(cp => ({ ...cp })) }));
    } else {
      setQuickFirmForm(p => ({ ...p, contact_persons: [] }));
    }
  };

  const handleCreateQuickFirm = async (e) => {
    e.preventDefault();
    setQuickFirmError(''); setQuickFirmSubmitting(true);
    try {
      const payload = { ...quickFirmForm, credit_days: parseInt(quickFirmForm.credit_days) || 30, contact_persons: quickFirmForm.contact_persons.filter(cp => cp.name) };
      const res = await createCustomerFirm(payload);
      toast.success('Customer created');
      setQuickFirmDialogOpen(false);
      fetchAllCustomerFirms();
      setSeForm(p => ({ ...p, customer_firm_id: res.data.id }));
    } catch (err) { setQuickFirmError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setQuickFirmSubmitting(false); }
  };

  const handleCreateFirm = async (e) => {
    e.preventDefault(); setFirmError(''); setFirmSubmitting(true);
    try {
      const payload = { ...firmForm, credit_days: parseInt(firmForm.credit_days) };
      if (editingFirm) {
        await updateCustomerFirm(editingFirm.id, payload);
        toast.success('Firm updated');
      } else {
        await createCustomerFirm(payload);
        toast.success('Firm created');
      }
      setFirmDialogOpen(false);
      fetchAllCustomerFirms();
    } catch (err) { setFirmError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setFirmSubmitting(false); }
  };

  const handleDeleteFirm = async (firm) => {
    if (!window.confirm(`Delete "${firm.company_name}"?`)) return;
    try { await deleteCustomerFirm(firm.id); toast.success('Deleted'); fetchAllCustomerFirms(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Tab change effect
  useEffect(() => {
    if (activeTab === 'sales-entries') fetchSalesEntries(seDateRange.start_date, seDateRange.end_date);
    if (activeTab === 'outstanding') fetchOutstanding();
    if (activeTab === 'receipt-outstanding') fetchReceiptOutstanding();
    if (activeTab === 'receipts') { fetchReceipts(receiptDateRange.start_date, receiptDateRange.end_date); if (!paymentOutstanding) fetchReceiptOutstanding(); }
    if (activeTab === 'customers') fetchAllCustomerFirms();
  }, [activeTab]); // eslint-disable-line

  const firmOptions = customerFirms.map(f => ({ value: f.id, label: f.company_name || f.name }));
  const productOptions = products.map(p => ({ value: p.id, label: `${p.name} (${p.group})` }));
  const poOptions = allSalesOrders.filter(o => o.status !== 'completed').map(o => {
    const firstProduct = o.line_items?.[0]?.product_name || o.product_name || '';
    const itemCount = (o.line_items || []).length;
    const label = `${o.so_number || 'SO'} — ${o.customer_firm_name} - ${firstProduct}${itemCount > 1 ? ` +${itemCount-1}` : ''} (${formatDate(o.date)})`;
    return { value: o.id, label };
  });
  const customerOptions = customers.map(s => ({ value: s.id, label: s.company_name }));

  // Print single document as PDF (uses authenticated axios + blob)
  const printSingle = async (path, fileName) => {
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1500);
    } catch (e) { toast.error('Failed to generate PDF'); }
  };

  if (loading && activeTab) return <div className="space-y-6"><Skeleton className="h-8 w-32" /><Skeleton className="h-64 w-full" /></div>;

  return (
    <>
    <SectionTiles
      sections={SECTION_DEFS}
      activeSection={activeTab}
      onSelect={(v) => {
        if (v === 'customer-ledger') { navigate('/ledger', { state: { tab: 'customer-ledger' } }); return; }
        if (v === 'notes') { navigate('/sales/credit-notes'); return; }
        setActiveTab(v); setSelectedSoIds([]); setSelectedSeIds([]); setSelectedFirmIds([]);
      }}
      pageTitle="Sales"
      pageDescription="Manage sales orders, entries, and customers"
      headerActions={(() => {
        const ExportBtn = ({ ep, name, params }) => (
          <ExportToolbar endpoint={ep} fileBaseName={name} params={params || {}} testId={`export-${activeTab}`} />
        );
        switch (activeTab) {
          case 'sales-orders': return (
            <div className="flex gap-2">
              <ExportBtn ep="/exports/sales-orders" name={`sales-orders-${soDateRange.start_date}_to_${soDateRange.end_date}`} params={{ start_date: soDateRange.start_date, end_date: soDateRange.end_date }} />
              <Button size="sm" onClick={() => { resetSoForm(); setSoDialogOpen(true); }} data-testid="add-so-btn"><Plus className="h-4 w-4 mr-1" />Add SO</Button>
            </div>
          );
          case 'sales-entries': return (
            <div className="flex gap-2">
              <ExportBtn ep="/exports/sales-entries" name={`sales-${seDateRange.start_date}_to_${seDateRange.end_date}`} params={{ start_date: seDateRange.start_date, end_date: seDateRange.end_date }} />
              <Button size="sm" onClick={() => { resetSeForm(); setSeDialogOpen(true); }} data-testid="add-se-btn"><Plus className="h-4 w-4 mr-1" />Add Entry</Button>
            </div>
          );
          case 'outstanding': return <ExportBtn ep="/exports/so-outstanding" name="so-outstanding" />;
          case 'receipt-outstanding': return (
            <div className="flex gap-2">
              <ExportBtn ep="/exports/sales-payment-outstanding" name="sales-outstanding" />
              <Button size="sm" onClick={() => openReceiptDialog()} data-testid="record-receipt-btn"><IndianRupee className="h-4 w-4 mr-1" />Record Receipt</Button>
            </div>
          );
          case 'receipts': return (
            <div className="flex gap-2">
              <ExportBtn ep="/exports/sale-receipts" name={`receipts-${receiptDateRange.start_date}_to_${receiptDateRange.end_date}`} params={{ start_date: receiptDateRange.start_date, end_date: receiptDateRange.end_date }} />
              <Button size="sm" onClick={() => openReceiptDialog()} data-testid="record-receipt-btn2"><IndianRupee className="h-4 w-4 mr-1" />Record Receipt</Button>
            </div>
          );
          case 'customers': return <Button size="sm" onClick={() => openFirmDialog()} data-testid="add-firm-btn"><Plus className="h-4 w-4 mr-1" />Add Customer</Button>;
          default: return null;
        }
      })()}
    >

      {activeTab === 'sales-orders' && (
        <div className="space-y-4">
          <DateRangeFilter startDate={soDateRange.start_date} endDate={soDateRange.end_date}
            onStartDateChange={(v) => setSoDateRange(prev => ({...prev, start_date: v}))}
            onEndDateChange={(v) => setSoDateRange(prev => ({...prev, end_date: v}))}
            onApply={(s, e) => { setSoDateRange({start_date: s, end_date: e}); fetchSalesOrders(s, e); }}
            activeQuick={soQuick} onQuickSelect={setSoQuick} />

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 cursor-pointer" onClick={toggleAllSo}><Checkbox checked={allSoSelected} className="pointer-events-none" data-testid="select-all-so" /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="SO #" sortKey="so_number" currentSortKey={soSortKey} sortDirection={soSortDir} onSort={handleSoSort} /></TableHead>
                      <TableHead><SortableHeader label="Date" sortKey="date" currentSortKey={soSortKey} sortDirection={soSortDir} onSort={handleSoSort} /></TableHead>
                      <TableHead><SortableHeader label="Customer" sortKey="customer_firm_name" currentSortKey={soSortKey} sortDirection={soSortDir} onSort={handleSoSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Product" sortKey="product_name" currentSortKey={soSortKey} sortDirection={soSortDir} onSort={handleSoSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell">Brand</TableHead>
                      <TableHead className="text-right"><SortableHeader label="Quantity" sortKey="quantity_kg" currentSortKey={soSortKey} sortDirection={soSortDir} onSort={handleSoSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell">Rate</TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Basic Amount" sortKey="amount" currentSortKey={soSortKey} sortDirection={soSortDir} onSort={handleSoSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Total" sortKey="grand_total" currentSortKey={soSortKey} sortDirection={soSortDir} onSort={handleSoSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Received" sortKey="received_kg" currentSortKey={soSortKey} sortDirection={soSortDir} onSort={handleSoSort} className="justify-end" /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Status" sortKey="status" currentSortKey={soSortKey} sortDirection={soSortDir} onSort={handleSoSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell">Remarks</TableHead>
                      <TableHead className="w-[60px] sm:w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedSOs.length === 0 ? (
                      <TableRow><TableCell colSpan={13} className="text-center py-8 text-muted-foreground">No sales orders found</TableCell></TableRow>
                    ) : sortedSOs.map(po => {
                      const li = po.line_items || [];
                      const firstItem = li[0] || {};
                      const productDisplay = li.length > 1 ? `${firstItem.product_name || ''} +${li.length-1}` : (firstItem.product_name || po.product_name || '-');
                      const brandDisplay = li.length === 1 ? (firstItem.brand || '-') : (li.length > 1 ? `${li.filter(i => i.brand).length} brands` : '-');
                      return (
                      <React.Fragment key={po.id}>
                        <TableRow
                          className={`cursor-pointer sm:cursor-default ${selectedSoIds.includes(po.id) ? 'bg-muted/50' : ''} ${expandedMobileId === po.id ? 'bg-muted/30' : ''}`}
                          onClick={() => setExpandedMobileId(expandedMobileId === po.id ? null : po.id)}
                          data-testid={`po-row-${po.id}`}
                        >
                          <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); togglePoSelect(po.id); }}><Checkbox checked={selectedSoIds.includes(po.id)} className="pointer-events-none" /></TableCell>
                          <TableCell className="font-mono text-xs hidden sm:table-cell">{po.so_number || '-'}</TableCell>
                          <TableCell className="text-sm">{formatDate(po.date)}</TableCell>
                          <TableCell className="font-medium text-sm">{po.customer_firm_name}</TableCell>
                          <TableCell className="hidden sm:table-cell">{productDisplay}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{brandDisplay}</TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            <div>{(po.quantity_kg || 0).toLocaleString()} KG</div>
                            <div className="text-xs text-muted-foreground">{toTons(po.quantity_kg)}</div>
                          </TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(firstItem.rate || po.rate || 0)}</TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(po.amount || (po.quantity_kg * (firstItem.rate || po.rate || 0)))}</TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(po.grand_total || po.amount || 0)}</TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{(po.received_kg || 0).toLocaleString()} KG</TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <Badge variant={po.status === 'completed' ? 'default' : po.status === 'partial' ? 'secondary' : 'outline'}>{po.status}</Badge>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell text-sm max-w-[200px]"><span className="whitespace-pre-wrap">{po.remarks || '-'}</span></TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Print PDF" onClick={(e) => { e.stopPropagation(); printSingle(`/exports/sales-orders/${po.id}/pdf`, po.so_number || 'sales-order'); }} data-testid={`print-so-${po.id}`}><Printer className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditSO(po)} data-testid={`edit-po-${po.id}`}><Pencil className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteSO(po)} data-testid={`delete-po-${po.id}`}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedMobileId === po.id && (
                          <TableRow className="sm:hidden bg-muted/20">
                            <TableCell colSpan={13}>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm py-1">
                                <span className="text-muted-foreground">SO #</span><span className="text-right font-mono">{po.so_number || '-'}</span>
                                {li.map((item, idx) => (
                                  <React.Fragment key={idx}>
                                    <span className="text-muted-foreground">Item {idx + 1}</span>
                                    <span className="text-right">{item.product_name} {item.brand ? `[${item.brand}]` : ''}</span>
                                    <span className="text-muted-foreground pl-2">Qty/Rate</span>
                                    <span className="text-right font-mono">{item.quantity_kg?.toLocaleString()} KG @ {formatCurrency(item.rate)}</span>
                                  </React.Fragment>
                                ))}
                                <span className="text-muted-foreground">Amount</span><span className="text-right">{formatCurrency(po.grand_total || po.amount || 0)}</span>
                                <span className="text-muted-foreground">Received</span><span className="text-right font-mono">{(po.received_kg || 0).toLocaleString()} KG</span>
                                <span className="text-muted-foreground">Status</span><span className="text-right"><Badge variant={po.status === 'completed' ? 'default' : 'outline'} className="text-xs">{po.status}</Badge></span>
                                {po.remarks && <><span className="text-muted-foreground">Remarks</span><span className="text-right">{po.remarks}</span></>}
                                <span className="text-muted-foreground">Remaining</span><span className="text-right font-mono">{(po.remaining_kg || 0).toLocaleString()} KG</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )})}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

        </div>
      )}

        {/* ========== PURCHASE ENTRIES TAB ========== */}
      {activeTab === 'sales-entries' && (
        <div className="space-y-4">
          <DateRangeFilter startDate={seDateRange.start_date} endDate={seDateRange.end_date}
            onStartDateChange={(v) => setSeDateRange(prev => ({...prev, start_date: v}))}
            onEndDateChange={(v) => setSeDateRange(prev => ({...prev, end_date: v}))}
            onApply={(s, e) => { setSeDateRange({start_date: s, end_date: e}); fetchSalesEntries(s, e); }}
            activeQuick={seQuick} onQuickSelect={setPeQuick} />

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 cursor-pointer" onClick={toggleAllPe}><Checkbox checked={allPeSelected} className="pointer-events-none" data-testid="select-all-pe" /></TableHead>
                      <TableHead><SortableHeader label="Date" sortKey="date" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead><SortableHeader label="Customer" sortKey="customer_firm_name" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Product" sortKey="product_name" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead className="text-right"><SortableHeader label="Quantity" sortKey="quantity_kg" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Amount" sortKey="amount" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Total" sortKey="grand_total" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} className="justify-end" /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Invoice" sortKey="invoice_number" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Vehicle" sortKey="vehicle_number" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Brand" sortKey="brand" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead className="w-[60px] sm:w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPEs.length === 0 ? (
                      <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No sales entries found</TableCell></TableRow>
                    ) : sortedPEs.map(entry => {
                      const li = entry.line_items || [];
                      const firstItem = li[0] || {};
                      const productDisplay = li.length > 1 ? `${firstItem.product_name || ''} +${li.length-1}` : (firstItem.product_name || entry.product_name || '-');
                      const brandDisplay = li.length === 1 ? (firstItem.brand || '-') : (li.some(i => i.brand) ? `${li.filter(i => i.brand).length} brands` : '-');
                      return (
                      <React.Fragment key={entry.id}>
                        <TableRow
                          className={`cursor-pointer sm:cursor-default ${selectedSeIds.includes(entry.id) ? 'bg-muted/50' : ''} ${expandedMobileId === entry.id ? 'bg-muted/30' : ''}`}
                          onClick={() => setExpandedMobileId(expandedMobileId === entry.id ? null : entry.id)}
                          data-testid={`pe-row-${entry.id}`}
                        >
                          <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); togglePeSelect(entry.id); }}><Checkbox checked={selectedSeIds.includes(entry.id)} className="pointer-events-none" /></TableCell>
                          <TableCell className="text-sm">{formatDate(entry.date)}</TableCell>
                          <TableCell className="font-medium text-sm">{entry.customer_firm_name}</TableCell>
                          <TableCell className="hidden sm:table-cell">{productDisplay}</TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            <div>{(entry.quantity_kg || 0).toLocaleString()} KG</div>
                            <div className="text-xs text-muted-foreground">{toTons(entry.quantity_kg)}</div>
                          </TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(entry.amount || 0)}</TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(entry.grand_total || entry.amount || 0)}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{entry.invoice_number || '-'}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{entry.vehicle_number || '-'}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{brandDisplay}</TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Print PDF" onClick={(e) => { e.stopPropagation(); printSingle(`/exports/sales-entries/${entry.id}/pdf`, entry.invoice_number || 'invoice'); }} data-testid={`print-se-${entry.id}`}><Printer className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditSE(entry)} data-testid={`edit-pe-${entry.id}`}><Pencil className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteSE(entry)} data-testid={`delete-pe-${entry.id}`}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedMobileId === entry.id && (
                          <TableRow className="sm:hidden bg-muted/20">
                            <TableCell colSpan={11}>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm py-1">
                                {li.map((item, idx) => (
                                  <React.Fragment key={idx}>
                                    <span className="text-muted-foreground">Item {idx + 1}</span>
                                    <span className="text-right">{item.product_name} {item.brand ? `[${item.brand}]` : ''}</span>
                                    <span className="text-muted-foreground pl-2">Qty/Rate</span>
                                    <span className="text-right font-mono">{item.quantity_kg?.toLocaleString()} KG @ {formatCurrency(item.rate)}</span>
                                  </React.Fragment>
                                ))}
                                <span className="text-muted-foreground">Amount</span><span className="text-right font-mono">{formatCurrency(entry.grand_total || entry.amount || 0)}</span>
                                <span className="text-muted-foreground">Invoice</span><span className="text-right">{entry.invoice_number || '-'}</span>
                                <span className="text-muted-foreground">Vehicle</span><span className="text-right">{entry.vehicle_number || '-'}</span>
                                {entry.remarks && <><span className="text-muted-foreground">Remarks</span><span className="text-right">{entry.remarks}</span></>}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )})}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

        {/* ========== OUTSTANDING TAB ========== */}
      {activeTab === 'outstanding' && (
        <div className="space-y-4">
          {outstandingLoading ? <Skeleton className="h-64 w-full" /> : outstanding && (
            <>
              {/* Product-wise Summary */}
              {outstanding.products && outstanding.products.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Product-wise Summary</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs sm:text-sm">Product</TableHead>
                          <TableHead className="text-right text-xs sm:text-sm">Ordered</TableHead>
                          <TableHead className="text-right text-xs sm:text-sm text-income">Received</TableHead>
                          <TableHead className="text-right text-xs sm:text-sm text-expense">Pending</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {outstanding.products.map(p => (
                          <React.Fragment key={p.product_id}>
                            <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setExpandedMobileId(expandedMobileId === `prod-${p.product_id}` ? null : `prod-${p.product_id}`)}>
                              <TableCell className="text-sm font-medium">{p.product_name}</TableCell>
                              <TableCell className="text-right font-mono text-sm">{toTons(p.total_ordered_kg)}</TableCell>
                              <TableCell className="text-right font-mono text-sm text-income">{toTons(p.total_received_kg)}</TableCell>
                              <TableCell className="text-right font-mono text-sm text-expense font-semibold">{toTons(p.total_remaining_kg)}</TableCell>
                            </TableRow>
                            {expandedMobileId === `prod-${p.product_id}` && p.orders && p.orders.length > 0 && (
                              <TableRow className="bg-muted/10">
                                <TableCell colSpan={4} className="p-0">
                                  {/* Desktop sub-table */}
                                  <div className="hidden sm:block px-4 py-2">
                                    <Table>
                                      <TableHeader><TableRow>
                                        <TableHead className="text-xs">SO #</TableHead>
                                        <TableHead className="text-xs">Brand</TableHead>
                                        <TableHead className="text-xs">Customer Firm</TableHead>
                                        <TableHead className="text-right text-xs">Ordered</TableHead>
                                        <TableHead className="text-right text-xs">Received</TableHead>
                                        <TableHead className="text-right text-xs text-expense">Pending</TableHead>
                                        <TableHead className="text-right text-xs">Rate</TableHead>
                                      </TableRow></TableHeader>
                                      <TableBody>
                                        {p.orders.map((o, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell className="text-xs font-mono">{o.so_number}</TableCell>
                                            <TableCell className="text-xs">{o.brand || '-'}</TableCell>
                                            <TableCell className="text-xs">{o.customer_firm_name}</TableCell>
                                            <TableCell className="text-right text-xs font-mono">{toTons(o.quantity_kg)}</TableCell>
                                            <TableCell className="text-right text-xs font-mono text-income">{toTons(o.received_kg)}</TableCell>
                                            <TableCell className="text-right text-xs font-mono text-expense">{toTons(o.pending_kg)}</TableCell>
                                            <TableCell className="text-right text-xs font-mono">{formatCurrency(o.rate)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                  {/* Mobile sub-cards */}
                                  <div className="sm:hidden px-3 py-2 space-y-2">
                                    {p.orders.map((o, idx) => (
                                      <div key={idx} className="bg-background border rounded px-2.5 py-2 text-xs space-y-1">
                                        <div className="flex items-center justify-between">
                                          <span className="font-mono font-semibold">{o.so_number}</span>
                                          <span className="font-mono">{formatCurrency(o.rate)}</span>
                                        </div>
                                        <div className="text-muted-foreground">{o.customer_firm_name} {o.brand ? `· ${o.brand}` : ''}</div>
                                        <div className="flex justify-between">
                                          <span className="font-mono">{toTons(o.quantity_kg)}</span>
                                          <span className="font-mono text-income">{toTons(o.received_kg)}</span>
                                          <span className="font-mono text-expense">{toTons(o.pending_kg)}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {outstanding.groups.map(group => (
                <Card key={group.group_id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="h-4 w-4" />{group.group_name}
                      <Badge variant="outline" className="ml-auto">{group.orders.length} POs</Badge>
                    </CardTitle>
                    <div className="grid grid-cols-3 gap-1 sm:gap-2 mt-2">
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Ordered</p>
                        <p className="font-mono font-semibold text-sm">{toTons(group.total_ordered_kg)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Received</p>
                        <p className="font-mono font-semibold text-income text-sm">{toTons(group.total_received_kg)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Remaining</p>
                        <p className="font-mono font-semibold text-expense text-sm">{toTons(group.total_remaining_kg)}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0 pb-0 pt-0">
                    {/* Desktop table view */}
                    <div className="hidden sm:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-sm">SO #</TableHead>
                            <TableHead className="text-sm">Date</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead>Brand</TableHead>
                            <TableHead className="text-right text-sm">Rate</TableHead>
                            <TableHead className="text-right text-sm text-income">Received</TableHead>
                            <TableHead className="text-right text-sm text-expense">Remaining</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.orders.map(o => (
                            <React.Fragment key={o.id}>
                              <TableRow
                                className={`cursor-pointer hover:bg-muted/50 ${expandedMobileId === `os-${o.id}` ? 'bg-muted/30' : ''}`}
                                onClick={() => setExpandedMobileId(expandedMobileId === `os-${o.id}` ? null : `os-${o.id}`)}
                                data-testid={`outstanding-po-${o.id}`}
                              >
                                <TableCell className="font-mono text-xs">{o.so_number || '-'}</TableCell>
                                <TableCell className="text-sm">{formatDate(o.date)}</TableCell>
                                <TableCell>{o.product_name}</TableCell>
                                <TableCell className="text-sm">{o.brand || '-'}</TableCell>
                                <TableCell className="text-right font-mono text-sm">{formatCurrency(o.rate || 0)}</TableCell>
                                <TableCell className="text-right font-mono text-sm text-income">{toTons(o.received_kg)}</TableCell>
                                <TableCell className="text-right font-mono text-sm text-expense">{toTons(o.remaining_kg)}</TableCell>
                              </TableRow>
                              {expandedMobileId === `os-${o.id}` && (
                                <TableRow className="bg-muted/10">
                                  <TableCell colSpan={7} className="p-0">
                                    <div className="px-4 py-3 space-y-3">
                                      <div className="flex flex-wrap items-center gap-3 text-xs">
                                        <Badge variant="outline" className="capitalize">{o.status}</Badge>
                                        {o.credit_days && <span className="text-muted-foreground">Credit: <span className="font-mono font-medium text-foreground">{o.credit_days} days</span></span>}
                                      </div>
                                      {o.remarks && <div className="text-xs text-muted-foreground bg-background border rounded px-2 py-1.5"><span className="font-medium text-foreground">Remarks:</span> {o.remarks}</div>}
                                      {o.firm_entries && o.firm_entries.length > 0 ? (
                                        <div className="rounded border overflow-hidden">
                                          <Table>
                                            <TableHeader><TableRow>
                                              <TableHead className="text-xs py-1.5">Firm</TableHead>
                                              <TableHead className="text-xs py-1.5">Invoice</TableHead>
                                              <TableHead className="text-xs py-1.5">Date</TableHead>
                                              <TableHead className="text-xs py-1.5">Vehicle</TableHead>
                                              <TableHead className="text-xs py-1.5">Brand</TableHead>
                                              <TableHead className="text-right text-xs py-1.5">Qty</TableHead>
                                              <TableHead className="text-right text-xs py-1.5">Rate</TableHead>
                                            </TableRow></TableHeader>
                                            <TableBody>
                                              {o.firm_entries.flatMap((fe) =>
                                                fe.entries.map((e, eIdx) => (
                                                  <TableRow key={`${fe.firm_name}-${eIdx}`}>
                                                    <TableCell className="text-xs py-1.5 font-medium">{fe.firm_name}</TableCell>
                                                    <TableCell className="text-xs py-1.5 font-mono">{e.invoice_number || '-'}</TableCell>
                                                    <TableCell className="text-xs py-1.5">{formatDate(e.date)}</TableCell>
                                                    <TableCell className="text-xs py-1.5">{e.vehicle_number || '-'}</TableCell>
                                                    <TableCell className="text-xs py-1.5">{e.brand || '-'}</TableCell>
                                                    <TableCell className="text-right text-xs py-1.5 font-mono">{toTons(e.quantity_kg)}</TableCell>
                                                    <TableCell className="text-right text-xs py-1.5 font-mono">{formatCurrency(e.rate)}</TableCell>
                                                  </TableRow>
                                                ))
                                              )}
                                            </TableBody>
                                          </Table>
                                        </div>
                                      ) : (
                                        <p className="text-xs text-muted-foreground text-center py-2">No sales entries yet</p>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile card view */}
                    <div className="sm:hidden divide-y">
                      {group.orders.map(o => (
                        <div key={o.id} data-testid={`outstanding-po-m-${o.id}`}>
                          <div
                            className={`px-3 py-2.5 cursor-pointer active:bg-muted/50 ${expandedMobileId === `os-${o.id}` ? 'bg-muted/30' : ''}`}
                            onClick={() => setExpandedMobileId(expandedMobileId === `os-${o.id}` ? null : `os-${o.id}`)}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-mono text-xs font-semibold">{o.so_number}</span>
                              <span className="text-xs text-muted-foreground">{formatDate(o.date)}</span>
                            </div>
                            <div className="text-sm">{o.product_name} {o.brand ? <span className="text-muted-foreground">({o.brand})</span> : ''}</div>
                            <div className="flex items-center justify-between mt-1.5 text-xs">
                              <span className="font-mono">{formatCurrency(o.rate || 0)}/KG</span>
                              <div className="flex gap-3">
                                <span className="font-mono text-income">{toTons(o.received_kg)}</span>
                                <span className="font-mono text-expense">{toTons(o.remaining_kg)}</span>
                              </div>
                            </div>
                          </div>
                          {expandedMobileId === `os-${o.id}` && (
                            <div className="bg-muted/10 px-3 py-2.5 space-y-2 border-t">
                              <div className="flex flex-wrap items-center gap-2 text-xs">
                                <Badge variant="outline" className="capitalize text-[10px]">{o.status}</Badge>
                                {o.credit_days && <span className="text-muted-foreground">Credit: <span className="font-mono font-medium text-foreground">{o.credit_days} days</span></span>}
                              </div>
                              {o.remarks && <div className="text-xs text-muted-foreground bg-background border rounded px-2 py-1.5 break-words"><span className="font-medium text-foreground">Remarks:</span> {o.remarks}</div>}
                              {o.firm_entries && o.firm_entries.length > 0 ? (
                                <div className="space-y-2">
                                  {o.firm_entries.flatMap((fe) =>
                                    fe.entries.map((e, eIdx) => (
                                      <div key={`${fe.firm_name}-${eIdx}`} className="bg-background border rounded px-2.5 py-2 text-xs space-y-1">
                                        <div className="flex items-center justify-between">
                                          <span className="font-semibold">{fe.firm_name}</span>
                                          <span className="font-mono">{formatDate(e.date)}</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
                                          <span>Invoice</span><span className="text-right text-foreground font-mono">{e.invoice_number || '-'}</span>
                                          <span>Vehicle</span><span className="text-right text-foreground">{e.vehicle_number || '-'}</span>
                                          <span>Brand</span><span className="text-right text-foreground">{e.brand || '-'}</span>
                                          <span>Qty</span><span className="text-right text-foreground font-mono">{toTons(e.quantity_kg)}</span>
                                          <span>Rate</span><span className="text-right text-foreground font-mono">{formatCurrency(e.rate)}</span>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground text-center py-2">No sales entries yet</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {outstanding.groups.length === 0 && <p className="text-center text-muted-foreground py-8">No outstanding sales orders</p>}
            </>
          )}
        </div>
      )}

      {/* ========== PURCHASE OUTSTANDING TAB ========== */}
      {activeTab === 'receipt-outstanding' && (
        <div className="space-y-4">
          {paymentOutstandingLoading ? <Skeleton className="h-64 w-full" /> : paymentOutstanding && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Total Bill</CardTitle></CardHeader><CardContent className="px-3 pb-3"><p className="text-base sm:text-lg font-bold font-mono">{formatCurrency(paymentOutstanding.total_amount)}</p></CardContent></Card>
                <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Paid</CardTitle></CardHeader><CardContent className="px-3 pb-3"><p className="text-base sm:text-lg font-bold font-mono text-income">{formatCurrency(paymentOutstanding.total_paid)}</p></CardContent></Card>
                <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Outstanding</CardTitle></CardHeader><CardContent className="px-3 pb-3"><p className="text-base sm:text-lg font-bold font-mono text-expense">{formatCurrency(paymentOutstanding.total_outstanding)}</p></CardContent></Card>
                <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Overdue</CardTitle></CardHeader><CardContent className="px-3 pb-3"><p className="text-base sm:text-lg font-bold font-mono text-destructive">{formatCurrency(paymentOutstanding.total_overdue)}</p></CardContent></Card>
              </div>
              {paymentOutstanding.firms.map(firm => (
                <Card key={firm.customer_firm_id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="h-4 w-4" />{firm.customer_firm_name}
                      <span className="text-xs text-muted-foreground font-normal">({firm.group_name})</span>
                    </CardTitle>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-2 mt-2">
                      <div className="text-center"><p className="text-xs text-muted-foreground">Bill</p><p className="font-mono font-semibold text-sm">{formatCurrency(firm.total_amount)}</p></div>
                      <div className="text-center"><p className="text-xs text-muted-foreground">Paid</p><p className="font-mono font-semibold text-income text-sm">{formatCurrency(firm.total_paid)}</p></div>
                      <div className="text-center"><p className="text-xs text-muted-foreground">Outstanding</p><p className="font-mono font-semibold text-expense text-sm">{formatCurrency(firm.total_outstanding)}</p></div>
                      <div className="text-center"><p className="text-xs text-muted-foreground">Overdue</p><p className="font-mono font-semibold text-destructive text-sm">{formatCurrency(firm.total_overdue)}</p></div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs sm:text-sm w-[80px]">Invoice</TableHead>
                            <TableHead className="text-xs sm:text-sm w-[100px]">Date</TableHead>
                            <TableHead className="hidden sm:table-cell text-xs w-[110px]">Due Date</TableHead>
                            <TableHead className="text-xs sm:text-sm text-center w-[70px]">Status</TableHead>
                            <TableHead className="text-right text-xs sm:text-sm w-[110px]">Bill</TableHead>
                            <TableHead className="text-right text-xs sm:text-sm text-income hidden sm:table-cell w-[100px]">Paid</TableHead>
                            <TableHead className="text-right text-xs sm:text-sm text-expense w-[110px]">Due</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {firm.entries.map(entry => (
                            <React.Fragment key={entry.id}>
                              <TableRow
                                className={`cursor-pointer hover:bg-muted/50 ${entry.is_overdue ? 'bg-destructive/5' : ''} ${expandedMobileId === `po-${entry.id}` ? 'bg-muted/30' : ''}`}
                                onClick={() => setExpandedMobileId(expandedMobileId === `po-${entry.id}` ? null : `po-${entry.id}`)}
                                data-testid={`receipt-outstanding-${entry.id}`}
                              >
                                <TableCell className="text-sm font-medium">{entry.invoice_number || '-'}</TableCell>
                                <TableCell className="text-sm whitespace-nowrap">{formatDate(entry.date)}</TableCell>
                                <TableCell className="hidden sm:table-cell text-sm whitespace-nowrap">
                                  <div>{formatDate(entry.due_date)}</div>
                                  {entry.is_overdue ? <span className="text-[10px] text-destructive font-semibold">{entry.overdue_days}d overdue</span> : <span className="text-[10px] text-muted-foreground">{entry.remaining_days}d remaining</span>}
                                </TableCell>
                                <TableCell className="text-center">
                                  {entry.is_overdue ? <Badge variant="destructive" className="text-[10px]">Overdue</Badge> : <Badge variant="outline" className="text-[10px]">{entry.remaining_days}d</Badge>}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm whitespace-nowrap">{formatCurrency(entry.grand_total)}</TableCell>
                                <TableCell className="text-right font-mono text-sm text-income hidden sm:table-cell whitespace-nowrap">{formatCurrency(entry.paid_amount)}</TableCell>
                                <TableCell className="text-right font-mono text-sm text-expense font-semibold whitespace-nowrap">{formatCurrency(entry.outstanding)}</TableCell>
                              </TableRow>
                              {expandedMobileId === `po-${entry.id}` && (
                                <TableRow className="sm:hidden bg-muted/20">
                                  <TableCell colSpan={7} className="py-2 px-4">
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                      <span className="text-muted-foreground">Product</span><span className="text-right">{entry.product_name}{entry.item_count > 1 ? ` +${entry.item_count - 1}` : ''}</span>
                                      <span className="text-muted-foreground">Due Date</span><span className="text-right">{formatDate(entry.due_date)}</span>
                                      <span className="text-muted-foreground">Days</span><span className={`text-right font-semibold ${entry.is_overdue ? 'text-destructive' : ''}`}>{entry.is_overdue ? `${entry.overdue_days}d overdue` : `${entry.remaining_days}d remaining`}</span>
                                      <span className="text-muted-foreground">Paid</span><span className="text-right font-mono text-income">{formatCurrency(entry.paid_amount)}</span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {paymentOutstanding.firms.length === 0 && <p className="text-center text-muted-foreground py-8">No outstanding receipts</p>}
            </>
          )}
        </div>
      )}

      {/* ========== PAYMENTS TAB ========== */}
      {activeTab === 'receipts' && (
        <div className="space-y-4">
          <DateRangeFilter startDate={receiptDateRange.start_date} endDate={receiptDateRange.end_date}
            onStartDateChange={(v) => setReceiptDateRange(prev => ({...prev, start_date: v}))}
            onEndDateChange={(v) => setReceiptDateRange(prev => ({...prev, end_date: v}))}
            onApply={(s, e) => { setReceiptDateRange({start_date: s, end_date: e}); fetchReceipts(s, e); }}
            activeQuick={receiptQuick} onQuickSelect={setReceiptQuick} />
          {paymentsLoading ? <Skeleton className="h-64 w-full" /> : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10 cursor-pointer" onClick={toggleAllPay}><Checkbox checked={allPaySelected} className="pointer-events-none" data-testid="select-all-receipts" /></TableHead>
                        <TableHead className="text-xs sm:text-sm">Date</TableHead>
                        <TableHead className="text-xs sm:text-sm">Customer</TableHead>
                        <TableHead className="text-xs sm:text-sm">Mode</TableHead>
                        <TableHead className="hidden sm:table-cell">Reference</TableHead>
                        <TableHead className="hidden sm:table-cell">Invoices</TableHead>
                        <TableHead className="text-right text-xs sm:text-sm">Amount</TableHead>
                        <TableHead className="text-xs sm:text-sm">Status</TableHead>
                        <TableHead className="text-right text-xs sm:text-sm hidden sm:table-cell">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {receipts.length === 0 ? (
                        <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No receipts recorded</TableCell></TableRow>
                      ) : receipts.map(p => (
                        <React.Fragment key={p.id}>
                          <TableRow
                            className={`cursor-pointer hover:bg-muted/50 ${selectedPayIds.includes(p.id) ? 'bg-muted/50' : ''} ${expandedMobileId === `pmt-${p.id}` ? 'bg-muted/30' : ''}`}
                            onClick={() => setExpandedMobileId(expandedMobileId === `pmt-${p.id}` ? null : `pmt-${p.id}`)}
                            data-testid={`payment-row-${p.id}`}
                          >
                            <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); togglePaySelect(p.id); }}><Checkbox checked={selectedPayIds.includes(p.id)} className="pointer-events-none" /></TableCell>
                            <TableCell className="text-sm whitespace-nowrap">{formatDate(p.date)}</TableCell>
                            <TableCell className="text-sm">{p.customer_firm_name}</TableCell>
                            <TableCell><Badge variant="outline" className="text-[10px] capitalize">{p.mode?.replace('_', ' ')}</Badge></TableCell>
                            <TableCell className="hidden sm:table-cell text-sm font-mono">{p.reference || '-'}</TableCell>
                            <TableCell className="hidden sm:table-cell text-xs">{(p.allocations || []).map(a => a.invoice_number || a.sales_entry_id?.slice(-4)).join(', ')}</TableCell>
                            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{formatCurrency(p.amount)}</TableCell>
                            <TableCell>
                              <Badge variant={p.status === 'completed' || p.status === 'cleared' ? 'default' : p.status === 'bounced' ? 'destructive' : 'secondary'} className="text-[10px]">
                                {p.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right hidden sm:table-cell">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7" title="Print PDF" onClick={(e) => { e.stopPropagation(); printSingle(`/exports/sale-receipts/${p.id}/pdf`, p.receipt_number || 'receipt'); }} data-testid={`print-receipt-${p.id}`}><Printer className="h-3.5 w-3.5" /></Button>
                                {p.status === 'pending' && (
                                  <>
                                    <Button variant="ghost" size="sm" className="h-7 text-xs text-income" onClick={(e) => { e.stopPropagation(); setClearingReceipt(p); setClearDate(getToday()); setClearDialogOpen(true); }} data-testid={`clear-pmt-${p.id}`}>Clear</Button>
                                    <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={(e) => { e.stopPropagation(); handleBounceReceipt(p); }} data-testid={`bounce-pmt-${p.id}`}>Bounce</Button>
                                  </>
                                )}
                                {(p.status === 'pending' || p.status === 'bounced') && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteReceipt(p); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          {expandedMobileId === `pmt-${p.id}` && (
                            <TableRow className="sm:hidden bg-muted/20">
                              <TableCell colSpan={9} className="py-2 px-4">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                  <span className="text-muted-foreground">Reference</span><span className="text-right font-mono">{p.reference || '-'}</span>
                                  <span className="text-muted-foreground">Bank</span><span className="text-right">{p.bank_name || '-'}</span>
                                  <span className="text-muted-foreground">Invoices</span><span className="text-right">{(p.allocations || []).map(a => a.invoice_number || '?').join(', ')}</span>
                                  {p.clear_date && <><span className="text-muted-foreground">Clear Date</span><span className="text-right">{formatDate(p.clear_date)}</span></>}
                                  {p.remarks && <><span className="text-muted-foreground">Remarks</span><span className="text-right">{p.remarks}</span></>}
                                </div>
                                {p.status === 'pending' && (
                                  <div className="flex gap-2 mt-2 pt-2 border-t border-border/40">
                                    <Button variant="default" size="sm" className="h-7 text-xs flex-1" onClick={(e) => { e.stopPropagation(); setClearingReceipt(p); setClearDate(getToday()); setClearDialogOpen(true); }}>Clear</Button>
                                    <Button variant="destructive" size="sm" className="h-7 text-xs flex-1" onClick={(e) => { e.stopPropagation(); handleBounceReceipt(p); }}>Bounce</Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Unified Receipt Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Record Receipt</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handlePaySubmit} className="space-y-4">
            {receiptError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{receiptError}</AlertDescription></Alert>}

            <div className="space-y-2">
              <Label>Customer</Label>
              <SearchableSelect
                options={payFirmOptions}
                value={receiptForm.customer_firm_id}
                onChange={(v) => selectPayFirm(v)}
                placeholder="Select customer..."
                data-testid="pay-customer"
              />
            </div>

            <div className="space-y-2">
              <Label>Receipt Mode</Label>
              <SearchableSelect
                options={modeOptions}
                value={receiptForm.mode}
                onChange={(v) => setReceiptForm(prev => ({...prev, mode: v}))}
                placeholder="Select mode..."
                data-testid="pay-mode"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" step="0.01" value={receiptForm.amount} onChange={(e) => updatePayAmount(e.target.value)} placeholder="0.00" data-testid="pay-amount" />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <DateInput fyStartYear={selectedFY} value={receiptForm.date} onChange={(e) => setReceiptForm(prev => ({...prev, date: e.target.value}))} required data-testid="pay-date" />
              </div>
            </div>

            {(receiptForm.mode === 'cheque' || receiptForm.mode === 'online' || receiptForm.mode === 'bank_transfer') && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{receiptForm.mode === 'cheque' ? 'Cheque Number' : 'Reference / UTR'}</Label>
                  <Input value={receiptForm.reference} onChange={(e) => setReceiptForm(prev => ({...prev, reference: e.target.value}))} placeholder={receiptForm.mode === 'cheque' ? 'Cheque #' : 'UTR / Ref #'} data-testid="pay-reference" />
                </div>
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input value={receiptForm.bank_name} onChange={(e) => setReceiptForm(prev => ({...prev, bank_name: e.target.value}))} placeholder="e.g. SBI, HDFC" data-testid="pay-bank" />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Remarks</Label>
              <textarea className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[56px] resize-y" value={receiptForm.remarks} onChange={(e) => setReceiptForm(prev => ({...prev, remarks: e.target.value}))} placeholder="Optional remarks..." data-testid="pay-remarks" />
            </div>

            {/* Invoice allocation - shown when customer selected */}
            {receiptForm.allocations.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Invoice Allocation (FIFO auto-adjusted)</Label>
                <div className="border rounded-md divide-y">
                  {receiptForm.allocations.map((alloc, idx) => (
                    <div key={alloc.sales_entry_id} className="grid grid-cols-[1fr_auto_100px] items-center gap-2 px-3 py-2">
                      <div>
                        <span className="text-sm font-medium">{alloc.invoice_number}</span>
                        <span className="text-xs text-muted-foreground ml-2">Due: {alloc.due_date ? formatDate(alloc.due_date) : '-'}</span>
                      </div>
                      <span className="text-sm font-mono text-expense">{formatCurrency(alloc.outstanding)}</span>
                      <Input
                        type="number" step="0.01" className="h-8 text-sm text-right"
                        value={alloc.amount} onChange={(e) => updateAllocation(idx, e.target.value)}
                        placeholder="0"
                        data-testid={`alloc-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {receiptForm.mode === 'cheque' && (
              <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">Cheque receipts stay pending until marked "Cleared".</p>
            )}

            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setReceiptDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={receiptSubmitting} data-testid="pay-submit">
                {receiptSubmitting ? 'Recording...' : `${receiptForm.mode === 'cheque' ? 'Record Cheque' : 'Record Receipt'} (${formatCurrency(parseFloat(receiptForm.amount) || 0)})`}
              </Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* Clear Cheque Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Clear Cheque</DialogTitle></DialogHeader>
          {clearingReceipt && (
            <div className="space-y-4">
              <div className="p-3 rounded-md bg-muted space-y-1">
                <p className="text-sm"><span className="text-muted-foreground">Cheque #:</span> <span className="font-mono font-medium">{clearingReceipt.reference}</span></p>
                <p className="text-sm"><span className="text-muted-foreground">Amount:</span> <span className="font-mono font-medium">{formatCurrency(clearingReceipt.amount)}</span></p>
                <p className="text-sm"><span className="text-muted-foreground">Customer:</span> {clearingReceipt.customer_firm_name}</p>
              </div>
              <div className="space-y-2">
                <Label>Receipt Clear Date</Label>
                <DateInput fyStartYear={selectedFY} value={clearDate} onChange={(e) => setClearDate(e.target.value)} required data-testid="clear-date-input" />
                <p className="text-xs text-muted-foreground">This date will be used in the payment ledger entry.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setClearDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleClearReceipt} disabled={clearSubmitting} data-testid="clear-confirm-btn">{clearSubmitting ? 'Clearing...' : 'Clear Cheque'}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

        {/* ========== SUPPLIERS TAB ========== */}
      {activeTab === 'customers' && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 cursor-pointer" onClick={toggleAllFirms}><Checkbox checked={customerFirmsSelected} className="pointer-events-none" data-testid="select-all-firms" /></TableHead>
                      <TableHead><SortableHeader label="Firm Name" sortKey="company_name" currentSortKey={firmSortKey} sortDirection={firmSortDir} onSort={handleFirmSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Email" sortKey="company_email" currentSortKey={firmSortKey} sortDirection={firmSortDir} onSort={handleFirmSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Credit Days" sortKey="credit_days" currentSortKey={firmSortKey} sortDirection={firmSortDir} onSort={handleFirmSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell">Contact</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedFirms.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No customers yet</TableCell></TableRow>
                    ) : sortedFirms.map(firm => (
                      <React.Fragment key={firm.id}>
                        <TableRow
                          className={`cursor-pointer sm:cursor-default ${selectedFirmIds.includes(firm.id) ? 'bg-muted/50' : ''} ${expandedMobileId === `f-${firm.id}` ? 'bg-muted/30' : ''}`}
                          onClick={() => setExpandedMobileId(expandedMobileId === `f-${firm.id}` ? null : `f-${firm.id}`)}
                          data-testid={`firm-row-${firm.id}`}
                        >
                          <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleFirmSelect(firm.id); }}><Checkbox checked={selectedFirmIds.includes(firm.id)} className="pointer-events-none" /></TableCell>
                          <TableCell className="font-medium">{firm.company_name}</TableCell>
                          <TableCell className="hidden sm:table-cell">{firm.company_email || '-'}</TableCell>
                          <TableCell className="hidden sm:table-cell">{firm.credit_days}d</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{firm.contact_persons?.[0]?.name || '-'}</TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditFirm(firm)}><Pencil className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteFirm(firm)}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedMobileId === `f-${firm.id}` && (
                          <TableRow className="sm:hidden bg-muted/20">
                            <TableCell colSpan={6}>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm py-1">
                                <span className="text-muted-foreground">Email</span><span className="text-right">{firm.company_email || '-'}</span>
                                <span className="text-muted-foreground">Credit Days</span><span className="text-right">{firm.credit_days}d</span>
                                {firm.contact_persons?.map((cp, i) => cp.name && (
                                  <React.Fragment key={i}>
                                    <span className="text-muted-foreground">Contact {i + 1}</span>
                                    <span className="text-right">{cp.name} {cp.mobile_number ? `(${cp.mobile_number})` : ''}</span>
                                  </React.Fragment>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== Bulk Action Bar ===== */}
      {(() => {
        const count = activeTab === 'sales-orders' ? selectedSoIds.length : activeTab === 'sales-entries' ? selectedSeIds.length : activeTab === 'customers' ? selectedFirmIds.length : 0;
        const clearFn = () => { setSelectedSoIds([]); setSelectedSeIds([]); setSelectedFirmIds([]); };
        const deleteFn = activeTab === 'sales-orders' ? handleBulkDeletePOs : activeTab === 'sales-entries' ? handleBulkDeletePEs : handleBulkDeleteFirms;
        if (count === 0) return null;
        return (
          <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
            <span className="text-sm font-medium">{count} selected</span>
            <Button variant="outline" size="sm" onClick={clearFn}>Clear</Button>
            <Button variant="destructive" size="sm" onClick={deleteFn} data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
          </div>
        );
      })()}

      {/* ===== PO Dialog ===== */}
      <Dialog open={soDialogOpen} onOpenChange={setSoDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingSO ? 'Edit Sales Order' : 'New Sales Order'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleCreateSO} className="space-y-4">
            {soError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{soError}</AlertDescription></Alert>}
            <div className="grid grid-cols-[1fr_80px_140px] gap-3 items-end">
              <div className="space-y-2"><Label>Customer Firm</Label>
                <SearchableSelect options={firmOptions} value={soForm.customer_firm_id} onChange={handleSOCustomerChange} onCreateNew={(name) => handleCreateFirmFromSelect(name, 'so')} placeholder="Select customer firm" createNewLabel="Add firm" data-testid="so-firm-select" />
              </div>
              <div className="space-y-2"><Label>Credit Days</Label><Input type="number" value={soForm.credit_days} onChange={(e) => setSoForm(p => ({...p, credit_days: e.target.value}))} placeholder="30" data-testid="so-credit-days" /></div>
              <div className="space-y-2"><Label>Date</Label><DateInput fyStartYear={selectedFY} value={soForm.date} onChange={(e) => { const v = e.target.value; setSoForm(p => ({...p, date: v})); if (v && !isDateInFY(v, selectedFY)) toast.error(getFYDateError(selectedFY, fyLabel)); else reLookupAllHSNRates({...soForm, date: v}, setSoForm); }} required data-testid="so-date-input" /></div>
            </div>
            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Line Items</Label>
                <div className="flex gap-1.5">
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={openNewProduct} title="New Product (Alt+P)" data-testid="so-new-product-btn"><Plus className="h-3 w-3 mr-1" />Product</Button>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addLineItem(setSoForm)} data-testid="so-add-line-btn"><Plus className="h-3 w-3 mr-1" />Item</Button>
                </div>
              </div>
              {soForm.line_items.map((li, idx) => {
                const prod = products.find(p => p.id === li.product_id);
                const brandOpts = prod?.brands?.filter(b => b)?.map(b => ({ value: b, label: b })) || [];
                const lineAmount = (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
                const lineTax = (parseFloat(li.cgst_amount) || 0) + (parseFloat(li.sgst_amount) || 0) + (parseFloat(li.igst_amount) || 0);
                const lineTotal = lineAmount + lineTax;
                const brandSelect = brandOpts.length > 0 ? (
                  <SearchableSelect options={[{value: '', label: 'None'}, ...brandOpts]} value={li.brand} onChange={(v) => updateLineItem(soForm, setSoForm, idx, 'brand', v)} onCreateNew={async (name) => { if (!li.product_id) return; try { const p = products.find(pp => pp.id === li.product_id); await updateProduct(li.product_id, { brands: [...(p?.brands || []), name] }); const r = await getProducts(); setProducts(r.data); updateLineItem(soForm, setSoForm, idx, 'brand', name); toast.success(`Brand added`); } catch (err) { console.error('Brand add failed:', err); toast.error('Failed'); } }} placeholder="Brand" createNewLabel="Add brand" className="h-8 text-xs" data-testid={`po-line-brand-${idx}`} />
                ) : (
                  <Input value={li.brand} onChange={(e) => updateLineItem(soForm, setSoForm, idx, 'brand', e.target.value)} placeholder="Brand" className="h-8 text-xs" data-testid={`po-line-brand-${idx}`} />
                );
                return (
                  <div key={idx} className="border rounded-lg px-3 py-2 relative" data-testid={`po-line-item-${idx}`}>
                    {soForm.line_items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" className="absolute top-1 right-1 h-5 w-5 text-destructive hover:text-destructive z-10" onClick={() => removeLineItem(setSoForm, idx)} data-testid={`po-remove-line-${idx}`}><Trash2 className="h-3 w-3" /></Button>
                    )}
                    {/* Desktop */}
                    <div className="hidden sm:block space-y-0.5">
                      <div className="grid grid-cols-[2.2fr_1.5fr_0.8fr_1.2fr_0.6fr_1fr_0.8fr_1fr] gap-2 items-end">
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Product</Label>}
                          <SearchableSelect options={productOptions} value={li.product_id} onChange={(v) => { updateLineItem(soForm, setSoForm, idx, 'product_id', v); updateLineItem(soForm, setSoForm, idx, 'brand', ''); autoFillLineItemGST(soForm, setSoForm, idx, v); if (!editingSO) autoFillLineItemFromLastSO(soForm, setSoForm, idx, v); }} placeholder="Product" className="h-8 text-xs" data-testid={`po-line-product-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Qty (KG)</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.quantity_kg} onChange={(e) => handleLineItemKgChange(soForm, setSoForm, idx, e.target.value)} data-testid={`po-line-qty-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Rate</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.rate} onChange={(e) => { updateLineItem(soForm, setSoForm, idx, 'rate', e.target.value); setTimeout(() => recalcLineItemGST(soForm, setSoForm, idx), 0); }} data-testid={`po-line-rate-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Brand</Label>}
                          {brandSelect}
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">GST%</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.gst_rate} onChange={(e) => { updateLineItem(soForm, setSoForm, idx, 'gst_rate', e.target.value); setTimeout(() => recalcLineItemGST(soForm, setSoForm, idx), 0); }} placeholder="%" data-testid={`po-line-gst-${idx}`} />
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Basic Amt</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono">{lineAmount > 0 ? formatCurrency(lineAmount) : '-'}</div>
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Tax</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono text-muted-foreground">{lineTax > 0 ? formatCurrency(lineTax) : '-'}</div>
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Total</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono font-semibold">{lineTotal > 0 ? formatCurrency(lineTotal) : '-'}</div>
                        </div>
                      </div>
                      {/* Sub-info: HSN below product, Alt Qty below Qty */}
                      <div className="grid grid-cols-[2.2fr_1.5fr_0.8fr_1.2fr_0.6fr_1fr_0.8fr_1fr] gap-2 text-[10px] text-muted-foreground font-mono">
                        <div>{li.hsn_code ? `HSN: ${li.hsn_code}` : ''}</div>
                        <div>{li.quantity_kg ? <><span className="float-right">{toTons(parseFloat(li.quantity_kg))}{li.quantity_bags ? ` / ${li.quantity_bags} Bags` : ''}</span></> : ''}</div>
                        <div></div><div></div><div></div><div></div><div></div><div></div>
                      </div>
                    </div>
                    {/* Mobile */}
                    <div className="sm:hidden space-y-2">
                      <div className="space-y-0.5">
                        <Label className="text-xs">Product</Label>
                        <SearchableSelect options={productOptions} value={li.product_id} onChange={(v) => { updateLineItem(soForm, setSoForm, idx, 'product_id', v); updateLineItem(soForm, setSoForm, idx, 'brand', ''); autoFillLineItemGST(soForm, setSoForm, idx, v); if (!editingSO) autoFillLineItemFromLastSO(soForm, setSoForm, idx, v); }} placeholder="Select product" data-testid={`po-line-product-m-${idx}`} />
                        {li.hsn_code && <div className="text-[10px] text-muted-foreground font-mono">HSN: {li.hsn_code}</div>}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-0.5">
                          <Label className="text-xs">Qty (KG)</Label>
                          <Input type="number" step="0.01" min="0" value={li.quantity_kg} onChange={(e) => handleLineItemKgChange(soForm, setSoForm, idx, e.target.value)} />
                          {li.quantity_kg && <div className="text-[10px] text-muted-foreground font-mono text-right">{toTons(parseFloat(li.quantity_kg))}{li.quantity_bags ? ` / ${li.quantity_bags} B` : ''}</div>}
                        </div>
                        <div className="space-y-0.5"><Label className="text-xs">Rate</Label><Input type="number" step="0.01" min="0" value={li.rate} onChange={(e) => { updateLineItem(soForm, setSoForm, idx, 'rate', e.target.value); setTimeout(() => recalcLineItemGST(soForm, setSoForm, idx), 0); }} /></div>
                        <div className="space-y-0.5"><Label className="text-xs">GST %</Label><Input type="number" step="0.01" min="0" value={li.gst_rate} onChange={(e) => { updateLineItem(soForm, setSoForm, idx, 'gst_rate', e.target.value); setTimeout(() => recalcLineItemGST(soForm, setSoForm, idx), 0); }} /></div>
                      </div>
                      <div className="space-y-0.5"><Label className="text-xs">Brand</Label>{brandSelect}</div>
                      <div className="flex justify-between items-center text-xs pt-1 border-t">
                        <span className="text-muted-foreground">Basic: {lineAmount > 0 ? formatCurrency(lineAmount) : '-'}{lineTax > 0 ? ` + Tax: ${formatCurrency(lineTax)}` : ''}</span>
                        <span className="font-mono font-semibold">{lineTotal > 0 ? formatCurrency(lineTotal) : '-'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Totals */}
            {(() => {
              const { subtotal, totalTax } = computeTotals(soForm.line_items, 0);
              const rawTotal = subtotal + totalTax;
              const autoRound = rawTotal > 0 ? Math.round(rawTotal) - rawTotal : 0;
              const roundOff = soForm.round_off !== '' ? parseFloat(soForm.round_off) || 0 : Math.round(autoRound * 100) / 100;
              const grand = rawTotal + roundOff;
              return (
                <div className="border-t pt-2 text-sm space-y-0.5">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono w-28 text-right">{formatCurrency(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="font-mono w-28 text-right">{formatCurrency(totalTax)}</span></div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground flex items-center gap-1">Round Off <Button type="button" variant="ghost" size="icon" className="h-4 w-4" onClick={() => setPoEditRoundOff(!poEditRoundOff)} data-testid="so-round-off-btn"><Pencil className="h-2.5 w-2.5" /></Button></span>
                    {poEditRoundOff ? (
                      <Input type="number" step="0.01" autoFocus className="w-28 h-7 text-xs text-right font-mono" value={soForm.round_off !== '' ? soForm.round_off : String(Math.round(autoRound * 100) / 100)} onChange={(e) => setSoForm(p => ({...p, round_off: e.target.value}))} onBlur={() => setPoEditRoundOff(false)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setPoEditRoundOff(false); } }} data-testid="so-round-off-input" />
                    ) : (
                      <span className="font-mono w-28 text-right">{formatCurrency(roundOff)}</span>
                    )}
                  </div>
                  <div className="flex justify-between font-semibold text-base border-t pt-1 mt-1"><span>Grand Total</span><span className="font-mono w-28 text-right">{formatCurrency(grand)}</span></div>
                </div>
              );
            })()}
            <div className="space-y-2"><Label>Remarks</Label><Textarea placeholder="Optional remarks... (Shift+Enter for new line)" rows={2} value={soForm.remarks} onChange={(e) => setSoForm(p => ({...p, remarks: e.target.value}))} data-testid="so-remarks" /></div>
            <DialogFooter>
              <FormShortcutHints shortcuts={[{key: 'Alt+P', label: 'New Product'}, {key: '⇧+Enter', label: 'New line'}]} />
              <Button type="button" variant="outline" onClick={() => setSoDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={soSubmitting || !soForm.customer_firm_id || !soForm.line_items.some(li => li.product_id)} data-testid="so-submit-btn">{soSubmitting ? 'Saving...' : editingSO ? 'Update' : 'Create SO'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* ===== PE Dialog ===== */}
      <Dialog open={seDialogOpen} onOpenChange={(v) => { setSeDialogOpen(v); if (!v) setEditingSE(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingSE ? 'Edit Sales Entry' : 'New Sales Entry'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleSubmitSE} className="space-y-3">
            {seError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{seError}</AlertDescription></Alert>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2 sm:col-span-1"><Label>Sales Order</Label>
                <SearchableSelect options={poOptions} value={seForm.sales_order_id} onChange={handlePePoChange} placeholder="Select PO" data-testid="pe-po-select" />
              </div>
              <div className="space-y-2 col-span-2 sm:col-span-1"><Label>Customer (Firm)</Label>
                <div className="flex gap-1.5">
                  <div className="flex-1"><SearchableSelect options={customerOptions} value={seForm.customer_firm_id} onChange={(v) => { const firm = customers.find(f => f.id === v) || customerFirms.find(f => f.id === v); setSeForm(p => ({...p, customer_firm_id: v, credit_days: firm ? String(firm.credit_days || 0) : p.credit_days })); }} placeholder="Select firm" data-testid="pe-customer-select" /></div>
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={openQuickFirm} title="New Customer (Alt+S)" data-testid="pe-new-customer-btn"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
            {selectedSOForEntry && (
              <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5 font-mono">
                {selectedSOForEntry.so_number} | Ordered: {(selectedSOForEntry.quantity_kg || 0).toLocaleString()} KG | Received: {(selectedSOForEntry.received_kg || 0).toLocaleString()} KG | Remaining: {(selectedSOForEntry.remaining_kg || 0).toLocaleString()} KG
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Date</Label><DateInput fyStartYear={selectedFY} value={seForm.date} onChange={(e) => { const v = e.target.value; setSeForm(p => ({...p, date: v})); if (v && !isDateInFY(v, selectedFY)) toast.error(getFYDateError(selectedFY, fyLabel)); else reLookupAllHSNRates({...seForm, date: v}, setSeForm); }} required data-testid="pe-date-input" /></div>
              <div className="space-y-1.5"><Label>Invoice #</Label><Input value={seForm.invoice_number} onChange={(e) => setSeForm(p => ({...p, invoice_number: e.target.value}))} placeholder="Optional" data-testid="pe-invoice" /></div>
              <div className="space-y-1.5"><Label>Vehicle #</Label><Input value={seForm.vehicle_number} onChange={(e) => setSeForm(p => ({...p, vehicle_number: e.target.value}))} placeholder="GJ-01-AB-1234" data-testid="pe-vehicle" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3 items-end">
              <div className="space-y-1.5">
                <Label>Credit Days</Label>
                <Input type="number" value={seForm.credit_days} onChange={(e) => setSeForm(p => ({...p, credit_days: e.target.value}))} placeholder="0" data-testid="pe-credit-days" />
              </div>
              {seForm.date && seForm.credit_days && (() => {
                try {
                  const d = new Date(seForm.date);
                  d.setDate(d.getDate() + parseInt(seForm.credit_days));
                  const dueStr = d.toISOString().split('T')[0];
                  const now = new Date(); now.setHours(0,0,0,0);
                  const diff = Math.ceil((d - now) / 86400000);
                  return (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Due Date</Label>
                        <p className="text-sm font-mono font-medium h-9 flex items-center">{formatDate(dueStr)}</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Remaining</Label>
                        <p className={`text-sm font-semibold h-9 flex items-center ${diff < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                          {diff < 0 ? `${Math.abs(diff)}d overdue` : `${diff}d remaining`}
                        </p>
                      </div>
                    </>
                  );
                } catch (err) { console.error('Credit days calc failed:', err); return null; }
              })()}
            </div>
            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Line Items</Label>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addLineItem(setSeForm)} data-testid="pe-add-line-btn"><Plus className="h-3 w-3 mr-1" />Item</Button>
              </div>
              {seForm.line_items.map((li, idx) => {
                const prod = products.find(p => p.id === li.product_id);
                const brandOpts = prod?.brands?.filter(b => b)?.map(b => ({ value: b, label: b })) || [];
                const lineAmount = (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
                const lineTax = (parseFloat(li.cgst_amount) || 0) + (parseFloat(li.sgst_amount) || 0) + (parseFloat(li.igst_amount) || 0);
                const lineTotal = lineAmount + lineTax;
                const brandSelect = brandOpts.length > 0 ? (
                  <SearchableSelect options={[{value: '', label: 'None'}, ...brandOpts]} value={li.brand} onChange={(v) => updateLineItem(seForm, setSeForm, idx, 'brand', v)} placeholder="Brand" className="h-8 text-xs" data-testid={`pe-line-brand-${idx}`} />
                ) : (
                  <Input value={li.brand} onChange={(e) => updateLineItem(seForm, setSeForm, idx, 'brand', e.target.value)} placeholder="Brand" className="h-8 text-xs" data-testid={`pe-line-brand-${idx}`} />
                );
                return (
                  <div key={idx} className="border rounded-lg px-3 py-2 relative" data-testid={`pe-line-item-${idx}`}>
                    {seForm.line_items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" className="absolute top-1 right-1 h-5 w-5 text-destructive hover:text-destructive z-10" onClick={() => removeLineItem(setSeForm, idx)} data-testid={`pe-remove-line-${idx}`}><Trash2 className="h-3 w-3" /></Button>
                    )}
                    {/* Desktop */}
                    <div className="hidden sm:block space-y-0.5">
                      <div className="grid grid-cols-[2.2fr_1.5fr_0.8fr_1.2fr_0.6fr_1fr_0.8fr_1fr] gap-2 items-end">
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Product</Label>}
                          <SearchableSelect options={productOptions} value={li.product_id} onChange={(v) => { updateLineItem(seForm, setSeForm, idx, 'product_id', v); updateLineItem(seForm, setSeForm, idx, 'brand', ''); autoFillLineItemGST(seForm, setSeForm, idx, v); }} placeholder="Product" className="h-8 text-xs" data-testid={`pe-line-product-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Qty (KG)</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.quantity_kg} onChange={(e) => handleLineItemKgChange(seForm, setSeForm, idx, e.target.value)} data-testid={`pe-line-qty-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Rate</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.rate} onChange={(e) => { updateLineItem(seForm, setSeForm, idx, 'rate', e.target.value); setTimeout(() => recalcLineItemGST(seForm, setSeForm, idx), 0); }} data-testid={`pe-line-rate-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Brand</Label>}
                          {brandSelect}
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">GST%</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.gst_rate} onChange={(e) => { updateLineItem(seForm, setSeForm, idx, 'gst_rate', e.target.value); setTimeout(() => recalcLineItemGST(seForm, setSeForm, idx), 0); }} placeholder="%" data-testid={`pe-line-gst-${idx}`} />
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Basic Amt</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono">{lineAmount > 0 ? formatCurrency(lineAmount) : '-'}</div>
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Tax</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono text-muted-foreground">{lineTax > 0 ? formatCurrency(lineTax) : '-'}</div>
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Total</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono font-semibold">{lineTotal > 0 ? formatCurrency(lineTotal) : '-'}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-[2.2fr_1.5fr_0.8fr_1.2fr_0.6fr_1fr_0.8fr_1fr] gap-2 text-[10px] text-muted-foreground font-mono">
                        <div>{li.hsn_code ? `HSN: ${li.hsn_code}` : ''}</div>
                        <div>{li.quantity_kg ? <><span className="float-right">{toTons(parseFloat(li.quantity_kg))}{li.quantity_bags ? ` / ${li.quantity_bags} Bags` : ''}</span></> : ''}</div>
                        <div></div><div></div><div></div><div></div><div></div><div></div>
                      </div>
                    </div>
                    {/* Mobile */}
                    <div className="sm:hidden space-y-2">
                      <div className="space-y-0.5">
                        <Label className="text-xs">Product</Label>
                        <SearchableSelect options={productOptions} value={li.product_id} onChange={(v) => { updateLineItem(seForm, setSeForm, idx, 'product_id', v); updateLineItem(seForm, setSeForm, idx, 'brand', ''); autoFillLineItemGST(seForm, setSeForm, idx, v); }} placeholder="Select product" data-testid={`pe-line-product-m-${idx}`} />
                        {li.hsn_code && <div className="text-[10px] text-muted-foreground font-mono">HSN: {li.hsn_code}</div>}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-0.5">
                          <Label className="text-xs">Qty (KG)</Label>
                          <Input type="number" step="0.01" min="0" value={li.quantity_kg} onChange={(e) => handleLineItemKgChange(seForm, setSeForm, idx, e.target.value)} />
                          {li.quantity_kg && <div className="text-[10px] text-muted-foreground font-mono text-right">{toTons(parseFloat(li.quantity_kg))}{li.quantity_bags ? ` / ${li.quantity_bags} B` : ''}</div>}
                        </div>
                        <div className="space-y-0.5"><Label className="text-xs">Rate</Label><Input type="number" step="0.01" min="0" value={li.rate} onChange={(e) => { updateLineItem(seForm, setSeForm, idx, 'rate', e.target.value); setTimeout(() => recalcLineItemGST(seForm, setSeForm, idx), 0); }} /></div>
                        <div className="space-y-0.5"><Label className="text-xs">GST %</Label><Input type="number" step="0.01" min="0" value={li.gst_rate} onChange={(e) => { updateLineItem(seForm, setSeForm, idx, 'gst_rate', e.target.value); setTimeout(() => recalcLineItemGST(seForm, setSeForm, idx), 0); }} /></div>
                      </div>
                      <div className="space-y-0.5"><Label className="text-xs">Brand</Label>{brandSelect}</div>
                      <div className="flex justify-between items-center text-xs pt-1 border-t">
                        <span className="text-muted-foreground">Basic: {lineAmount > 0 ? formatCurrency(lineAmount) : '-'}{lineTax > 0 ? ` + Tax: ${formatCurrency(lineTax)}` : ''}</span>
                        <span className="font-mono font-semibold">{lineTotal > 0 ? formatCurrency(lineTotal) : '-'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Totals */}
            {(() => {
              const { subtotal, totalTax } = computeTotals(seForm.line_items, 0);
              const rawTotal = subtotal + totalTax;
              const autoRound = rawTotal > 0 ? Math.round(rawTotal) - rawTotal : 0;
              const roundOff = seForm.round_off !== '' ? parseFloat(seForm.round_off) || 0 : Math.round(autoRound * 100) / 100;
              const grand = rawTotal + roundOff;
              return (
                <div className="border-t pt-2 text-sm space-y-0.5">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono w-28 text-right">{formatCurrency(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="font-mono w-28 text-right">{formatCurrency(totalTax)}</span></div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground flex items-center gap-1">Round Off <Button type="button" variant="ghost" size="icon" className="h-4 w-4" onClick={() => setPeEditRoundOff(!peEditRoundOff)} data-testid="pe-round-off-btn"><Pencil className="h-2.5 w-2.5" /></Button></span>
                    {peEditRoundOff ? (
                      <Input type="number" step="0.01" autoFocus className="w-28 h-7 text-xs text-right font-mono" value={seForm.round_off !== '' ? seForm.round_off : String(Math.round(autoRound * 100) / 100)} onChange={(e) => setSeForm(p => ({...p, round_off: e.target.value}))} onBlur={() => setPeEditRoundOff(false)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setPeEditRoundOff(false); } }} data-testid="pe-round-off-input" />
                    ) : (
                      <span className="font-mono w-28 text-right">{formatCurrency(roundOff)}</span>
                    )}
                  </div>
                  <div className="flex justify-between font-semibold text-base border-t pt-1 mt-1"><span>Grand Total</span><span className="font-mono w-28 text-right">{formatCurrency(grand)}</span></div>
                </div>
              );
            })()}
            <div className="space-y-1.5"><Label>Remarks</Label><Textarea placeholder="Optional..." rows={2} value={seForm.remarks} onChange={(e) => setSeForm(p => ({...p, remarks: e.target.value}))} data-testid="pe-remarks" /></div>
            <DialogFooter>
              <FormShortcutHints shortcuts={[{key: 'Alt+S', label: 'New Customer'}, {key: '⇧+Enter', label: 'New line'}]} />
              <Button type="button" variant="outline" onClick={() => setSeDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={seSubmitting || !seForm.sales_order_id || !seForm.customer_firm_id || !seForm.line_items.some(li => li.product_id)} data-testid="pe-submit-btn">{seSubmitting ? 'Saving...' : editingSE ? 'Update' : 'Add Entry'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* ===== Firm Dialog ===== */}
      <Dialog open={firmDialogOpen} onOpenChange={setFirmDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" onKeyDownCapture={(e) => { if (e.altKey && e.code === 'KeyC') { e.preventDefault(); e.stopPropagation(); const el = document.activeElement; if (el && el.tagName === 'INPUT') el.blur(); setFirmForm(p => ({...p, contact_persons: [...p.contact_persons, {...emptyContact}]})); if (el) setTimeout(() => el.focus(), 0); } }}>
          <DialogHeader><DialogTitle>{editingFirm ? 'Edit Firm' : 'Add Firm'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleCreateFirm} className="space-y-4">
            {firmError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{firmError}</AlertDescription></Alert>}
            <div className="space-y-2"><Label>Company Name</Label><Input value={firmForm.company_name} onChange={(e) => setFirmForm(p => ({...p, company_name: e.target.value}))} placeholder="Firm name" required data-testid="firm-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={firmForm.company_email} onChange={(e) => setFirmForm(p => ({...p, company_email: e.target.value}))} placeholder="Optional" data-testid="firm-email-input" /></div>
              <div className="space-y-2"><Label>Credit Days</Label><Input type="number" min="0" value={firmForm.credit_days} onChange={(e) => setFirmForm(p => ({...p, credit_days: e.target.value}))} data-testid="firm-credit-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>GSTIN</Label>
              <Input value={firmForm.gstin} onChange={(e) => setFirmForm(p => ({...p, gstin: e.target.value.toUpperCase()}))} placeholder="e.g. 24ABCDE1234F1Z5" maxLength={15} className="font-mono uppercase" data-testid="firm-gstin-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>State</Label>
                <SearchableSelect options={INDIAN_STATES.map(s => ({ value: s.name, label: `${s.code} - ${s.name}` }))} value={firmForm.state} onChange={(v) => { const st = INDIAN_STATES.find(s => s.name === v); setFirmForm(p => ({...p, state: v, state_code: st?.code || ''})); }} placeholder="Select state" data-testid="firm-state-select" />
              </div>
              <div className="space-y-2"><Label>State Code</Label><Input value={firmForm.state_code} readOnly className="bg-muted font-mono" data-testid="firm-state-code" /></div>
            </div>
            <div className="space-y-2">
              <Label>Contact Persons</Label>
              {firmForm.contact_persons.map((cp, idx) => (
                <div key={idx} className="grid grid-cols-2 gap-2 p-2 border rounded">
                  <Input placeholder="Name" value={cp.name} onChange={(e) => { const cps = [...firmForm.contact_persons]; cps[idx] = {...cps[idx], name: e.target.value}; setFirmForm(p => ({...p, contact_persons: cps})); }} />
                  <PhoneInput value={cp.mobile_number} onChange={(e) => { const cps = [...firmForm.contact_persons]; cps[idx] = {...cps[idx], mobile_number: e.target.value}; setFirmForm(p => ({...p, contact_persons: cps})); }} />
                  <Input placeholder="Department" value={cp.department} onChange={(e) => { const cps = [...firmForm.contact_persons]; cps[idx] = {...cps[idx], department: e.target.value}; setFirmForm(p => ({...p, contact_persons: cps})); }} />
                  <Input placeholder="Email" value={cp.email} onChange={(e) => { const cps = [...firmForm.contact_persons]; cps[idx] = {...cps[idx], email: e.target.value}; setFirmForm(p => ({...p, contact_persons: cps})); }} />
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setFirmForm(p => ({...p, contact_persons: [...p.contact_persons, {...emptyContact}]}))}>+ Add Contact <kbd className="ml-1.5 px-1 py-0.5 text-[10px] font-mono bg-muted border rounded">Alt+C</kbd></Button>
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setFirmDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={firmSubmitting || !firmForm.company_name} data-testid="firm-submit-btn">{firmSubmitting ? 'Saving...' : editingFirm ? 'Update' : 'Add Firm'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* ===== Quick Product Creation Dialog ===== */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Product</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleCreateProduct} className="space-y-3">
            {productError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{productError}</AlertDescription></Alert>}
            <div className="space-y-2"><Label>Name</Label><Input autoFocus value={productForm.name} onChange={(e) => setProductForm(p => ({...p, name: e.target.value}))} placeholder="e.g. T Gyp" required data-testid="new-product-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Category</Label>
                <SearchableSelect options={[{value: 'raw_material', label: 'Raw Material'}, {value: 'in_process', label: 'In-Process'}, {value: 'finished_goods', label: 'Finished Goods'}]} value={productForm.category} onChange={(v) => setProductForm(p => ({...p, category: v}))} placeholder="Select category" data-testid="new-product-category" />
              </div>
              <div className="space-y-2"><Label>Group</Label>
                <SearchableSelect options={[...new Set(products.map(p => p.group))].filter(Boolean).map(g => ({value: g, label: g}))} value={productForm.group} onChange={(v) => setProductForm(p => ({...p, group: v}))} onCreateNew={(val) => setProductForm(p => ({...p, group: val}))} placeholder="Select or type group" createNewLabel="Add group" data-testid="new-product-group" />
              </div>
            </div>
            <div className="space-y-2"><Label>KG per Bag <span className="text-muted-foreground text-xs">(optional, default 50)</span></Label><Input type="number" step="0.01" min="0" value={productForm.kg_per_bag} onChange={(e) => setProductForm(p => ({...p, kg_per_bag: e.target.value}))} placeholder="e.g. 50" data-testid="new-product-kgperbag" /></div>
            <div className="space-y-2"><Label>Brands <span className="text-muted-foreground text-xs">(comma separated)</span></Label><Input value={productForm.brands} onChange={(e) => setProductForm(p => ({...p, brands: e.target.value}))} placeholder="e.g. Ambuja, Sanstar, Sayaji" data-testid="new-product-brands" /></div>
            <div className="space-y-2"><Label>Description <span className="text-muted-foreground text-xs">(optional)</span></Label><Input value={productForm.description} onChange={(e) => setProductForm(p => ({...p, description: e.target.value}))} placeholder="Product description" data-testid="new-product-description" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>HSN Code</Label>
                <SearchableSelect options={(() => { const seen = new Set(); return hsnCodes.filter(h => { if (seen.has(h.hsn_code)) return false; seen.add(h.hsn_code); return true; }).map(h => ({ value: h.hsn_code, label: `${h.hsn_code} - ${h.description || 'N/A'}` })); })()} value={productForm.hsn_code} onChange={(v) => { const hsn = hsnCodes.filter(h => h.hsn_code === v).sort((a, b) => (b.applicable_from || '').localeCompare(a.applicable_from || ''))[0]; setProductForm(p => ({...p, hsn_code: v, default_gst_rate: hsn ? String(hsn.gst_rate) : p.default_gst_rate})); }} placeholder="Select HSN" data-testid="new-product-hsn" />
              </div>
              <div className="space-y-2"><Label>Default GST Rate %</Label><Input type="number" step="0.01" value={productForm.default_gst_rate} onChange={(e) => setProductForm(p => ({...p, default_gst_rate: e.target.value}))} placeholder="e.g. 18" data-testid="new-product-gst-rate" /></div>
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={productSubmitting || !productForm.name || !productForm.category || !productForm.group} data-testid="new-product-submit">{productSubmitting ? 'Creating...' : 'Create Product'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* ===== Quick Customer Creation Dialog (from PE form) ===== */}
      <Dialog open={quickFirmDialogOpen} onOpenChange={setQuickFirmDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New Customer</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleCreateQuickFirm} className="space-y-3">
            {quickFirmError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{quickFirmError}</AlertDescription></Alert>}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Company Name</Label><Input autoFocus value={quickFirmForm.company_name} onChange={(e) => setQuickFirmForm(p => ({...p, company_name: e.target.value}))} placeholder="Firm name" required data-testid="quick-firm-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={quickFirmForm.company_email} onChange={(e) => setQuickFirmForm(p => ({...p, company_email: e.target.value}))} placeholder="Optional" data-testid="quick-firm-email" /></div>
              <div className="space-y-1.5"><Label>Credit Days</Label><Input type="number" min="0" value={quickFirmForm.credit_days} onChange={(e) => setQuickFirmForm(p => ({...p, credit_days: e.target.value}))} data-testid="quick-firm-credit" /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Contact Persons {quickFirmForm.contact_persons.length > 0 && <span className="text-xs text-muted-foreground">(auto-fetched from group)</span>}</Label>
              <div className="space-y-1.5">
                {quickFirmForm.contact_persons.map((cp, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_1fr_0.8fr_1fr_auto] gap-2 items-center">
                    <Input className="h-8 text-sm" placeholder="Name" value={cp.name} onChange={(e) => { const cps = [...quickFirmForm.contact_persons]; cps[idx] = {...cps[idx], name: e.target.value}; setQuickFirmForm(p => ({...p, contact_persons: cps})); }} />
                    <Input className="h-8 text-sm" placeholder="Mobile number" value={cp.mobile_number} onChange={(e) => { const cps = [...quickFirmForm.contact_persons]; cps[idx] = {...cps[idx], mobile_number: e.target.value}; setQuickFirmForm(p => ({...p, contact_persons: cps})); }} />
                    <Input className="h-8 text-sm" placeholder="Dept" value={cp.department} onChange={(e) => { const cps = [...quickFirmForm.contact_persons]; cps[idx] = {...cps[idx], department: e.target.value}; setQuickFirmForm(p => ({...p, contact_persons: cps})); }} />
                    <Input className="h-8 text-sm" placeholder="Email" value={cp.email} onChange={(e) => { const cps = [...quickFirmForm.contact_persons]; cps[idx] = {...cps[idx], email: e.target.value}; setQuickFirmForm(p => ({...p, contact_persons: cps})); }} />
                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => { const cps = quickFirmForm.contact_persons.filter((_, i) => i !== idx); setQuickFirmForm(p => ({...p, contact_persons: cps})); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setQuickFirmForm(p => ({...p, contact_persons: [...p.contact_persons, {name: '', mobile_number: '', department: '', email: ''}]}))}>+ Add Contact</Button>
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setQuickFirmDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={quickFirmSubmitting || !quickFirmForm.company_name} data-testid="quick-firm-submit">{quickFirmSubmitting ? 'Creating...' : 'Add Customer'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>
    </SectionTiles>
    {selectedPayIds.length > 0 && (
      <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
        <span className="text-sm font-medium">{selectedPayIds.length} selected</span>
        <Button variant="outline" size="sm" onClick={() => setSelectedPayIds([])} data-testid="clear-selection-btn">Clear</Button>
        <Button variant="destructive" size="sm" onClick={handleBulkDeleteReceipts} data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
      </div>
    )}
    </>
  );
};

export default Sales;
